import React, { useEffect, useMemo, useState } from 'react';
import { Star, Plus, Search, RotateCcw, Layers, CheckCircle2, XCircle, FolderTree, ImagePlus, FileText, Files, Type, Banknote, Pencil, Power, Trash2, X, SlidersHorizontal, Clock, Timer, CalendarDays, Eye, ArrowRight, Languages, Truck, Mail, MailCheck, Store, Zap, Landmark, AlertTriangle } from 'lucide-react';
import '../../components/dashboard/DashForm.css';
import './AdminOrders.css';
import '../../components/dashboard/AdminTranslatorFilters.css';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { adminApi } from '../../api/client';
import { formatEur, tndToEur } from '../../utils/currency';
import { mainServiceGroups, mainServices } from '../../data/mainServices';
import { filterTypesFor, TRANSLATION_SLUGS, DOCUMENT_SLUGS } from '../../data/serviceFilters';
import { interpretingServices } from '../../data/interpretingServices';
import StatusBadge from '../../components/dashboard/StatusBadge';
import assetUrl from '../../assetUrl';
import DashModal from '../../components/dashboard/DashModal';
import MessengerPanel from '../../components/dashboard/MessengerPanel';
import NotificationsPanel from '../../components/dashboard/NotificationsPanel';
import Pagination, { usePagination } from '../../components/dashboard/Pagination';
import { useCatalogLanguages } from '../../data/subServicesStore';
import { allCountries } from '../../data/quoteFormOptions';
import { flagEmoji, langFlagUrl } from '../../utils/langFlags';

export function AdminQuotesPage() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const orderFilter = searchParams.get('order');
  const orderFilterCode = searchParams.get('code');
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [quotes, setQuotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    estimatedPrice: '',
    deadline: '',
    adminNotes: '',
    send: true,
  });
  const [createForm, setCreateForm] = useState({
    clientId: '',
    orderId: '',
    type: 'Traduction',
    sourceLang: 'FR',
    targetLang: 'AR',
    estimatedPrice: '',
    details: { translation: '', proofreading: '', verification: '', delivery: '', urgency: '' },
    message: '',
    send: true,
  });
  const [msg, setMsg] = useState('');
  const [orders, setOrders] = useState([]);
  const { languages: catalogLangs } = useCatalogLanguages();
  const visibleQuotes = orderFilter ? quotes.filter((q) => String(q.orderId) === String(orderFilter)) : quotes;
  const quotesPg = usePagination(visibleQuotes, 6);

  const reload = () => adminApi.quotes().then((res) => setQuotes(res.quotes || [])).catch(() => setQuotes([]));

  useEffect(() => {
    reload();
    adminApi.users().then((res) => setUsers((res.users || []).filter((u) => u.role === 'client'))).catch(() => setUsers([]));
    adminApi.orders({ status: 'pending,request_sent' }).then((res) => setOrders(res.orders || [])).catch(() => setOrders([]));
  }, []);

  const openEdit = (q) => {
    setEditing(q);
    setForm({
      estimatedPrice: q.estimatedPrice ?? '',
      deadline: q.deadline || '',
      adminNotes: q.adminNotes || '',
      send: true,
    });
  };

  const saveQuote = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setMsg('');
    try {
      await adminApi.updateQuote(editing.id, {
        estimatedPrice: form.estimatedPrice === '' ? undefined : Number(form.estimatedPrice),
        deadline: form.deadline || null,
        adminNotes: form.adminNotes,
        send: form.send,
      });
      setEditing(null);
      setMsg(d.saved);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    }
  };

  const createQuote = async (e) => {
    e.preventDefault();
    setMsg('');
    const details = Object.fromEntries(
      Object.entries(createForm.details || {}).map(([k, v]) => [k, v === '' || v == null ? 0 : Number(v)])
    );
    const breakdownTotal = details.translation + details.proofreading + details.verification + details.delivery + details.urgency;
    const total = createForm.estimatedPrice === '' ? breakdownTotal : Number(createForm.estimatedPrice);
    try {
      await adminApi.createQuote({
        ...createForm,
        estimatedPrice: total || null,
        details,
      });
      setCreateOpen(false);
      setMsg(d.saved);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{a.quotesTitle}</h1>
        <p>{orderFilter ? `Devis de la commande ${orderFilterCode || ''}` : a.quotesSubtitle}</p>
      </header>
      <div className="dash-actions">
        <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>{a.createQuote}</button>
        {orderFilter && (
          <Link className="btn btn-outline" to="/admin/quotes">Tous les devis</Link>
        )}
      </div>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel">
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>{a.colCode || '#'}</th>
                <th>{a.colClient}</th>
                <th>{a.colLangs}</th>
                <th>{a.colTotal}</th>
                <th>{a.colStatus}</th>
                <th>{d.actions}</th>
              </tr>
            </thead>
            <tbody>
              {quotesPg.pageItems.map((q) => (
                <tr key={q.id}>
                  <td>{q.id}</td>
                  <td>{q.client || q.clientEmail || '—'}</td>
                  <td>{q.langs}</td>
                  <td>{q.estimatedPrice != null ? `${q.estimatedPrice} TND` : '—'}</td>
                  <td><StatusBadge status={q.status} labels={d.status} /></td>
                  <td>
                    <button type="button" className="dash-btn-sm" onClick={() => openEdit(q)}>{a.answerQuote}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={quotesPg.page} totalPages={quotesPg.totalPages} total={quotesPg.total} onChange={quotesPg.setPage} />
      </section>

      <DashModal open={Boolean(editing)} title={a.answerQuote} onClose={() => setEditing(null)}>
        <form className="dash-form" onSubmit={saveQuote} style={{ maxWidth: '100%' }}>
          <div className="dash-field">
            <label>{a.colTotal} (TND)</label>
            <input type="number" value={form.estimatedPrice} onChange={(e) => setForm({ ...form, estimatedPrice: e.target.value })} required />
          </div>
          <div className="dash-field">
            <label>{a.deadline}</label>
            <input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
          </div>
          <div className="dash-field">
            <label>{a.adminNotes}</label>
            <textarea rows={3} value={form.adminNotes} onChange={(e) => setForm({ ...form, adminNotes: e.target.value })} />
          </div>
          <label className="dash-check">
            <input type="checkbox" checked={form.send} onChange={(e) => setForm({ ...form, send: e.target.checked })} />
            {a.sendQuote}
          </label>
          <button type="submit" className="btn btn-primary">{d.save}</button>
        </form>
      </DashModal>

      <DashModal open={createOpen} title={a.createQuote} onClose={() => setCreateOpen(false)}>
        <form className="dash-form" onSubmit={createQuote} style={{ maxWidth: '100%' }}>
          <div className="dash-field">
            <label>{a.colClient}</label>
            <select required value={createForm.clientId} onChange={(e) => setCreateForm({ ...createForm, clientId: e.target.value })}>
              <option value="">—</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
              ))}
            </select>
          </div>
          <div className="dash-field">
            <label>Commande liée (optionnel)</label>
            <select value={createForm.orderId} onChange={(e) => setCreateForm({ ...createForm, orderId: e.target.value })}>
              <option value="">—</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>{o.code} — {o.document}</option>
              ))}
            </select>
          </div>
          <div className="dash-field">
            <label>{a.serviceType}</label>
            <input value={createForm.type} onChange={(e) => setCreateForm({ ...createForm, type: e.target.value })} />
          </div>
          <div className="dash-field">
            <label>{d.fromLang} / {d.toLang}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {catalogLangs.length ? (
                <>
                  <select value={createForm.sourceLang} onChange={(e) => setCreateForm({ ...createForm, sourceLang: e.target.value })}>
                    {catalogLangs.map((l) => <option key={`s-${l.code}`} value={l.code}>{[flagEmoji(l.flag), l.code].filter(Boolean).join(' ')}</option>)}
                  </select>
                  <select value={createForm.targetLang} onChange={(e) => setCreateForm({ ...createForm, targetLang: e.target.value })}>
                    {catalogLangs.map((l) => <option key={`t-${l.code}`} value={l.code}>{[flagEmoji(l.flag), l.code].filter(Boolean).join(' ')}</option>)}
                  </select>
                </>
              ) : (
                <>
                  <input value={createForm.sourceLang} onChange={(e) => setCreateForm({ ...createForm, sourceLang: e.target.value })} />
                  <input value={createForm.targetLang} onChange={(e) => setCreateForm({ ...createForm, targetLang: e.target.value })} />
                </>
              )}
            </div>
          </div>
          <div className="dash-field">
            <label>Détail du devis (TND)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                ['translation', 'Traduction'],
                ['proofreading', 'Relecture'],
                ['verification', 'Vérification'],
                ['delivery', 'Livraison'],
                ['urgency', 'Urgence'],
              ].map(([key, label]) => (
                <input
                  key={key}
                  type="number"
                  placeholder={label}
                  value={createForm.details[key]}
                  onChange={(e) => setCreateForm({ ...createForm, details: { ...createForm.details, [key]: e.target.value } })}
                />
              ))}
            </div>
          </div>
          <div className="dash-field">
            <label>{a.colTotal} (TND)</label>
            <input type="number" value={createForm.estimatedPrice} onChange={(e) => setCreateForm({ ...createForm, estimatedPrice: e.target.value })} placeholder="Total ou somme auto" />
          </div>
          <div className="dash-field">
            <label>{a.message}</label>
            <textarea rows={2} value={createForm.message} onChange={(e) => setCreateForm({ ...createForm, message: e.target.value })} />
          </div>
          <label className="dash-check">
            <input type="checkbox" checked={createForm.send} onChange={(e) => setCreateForm({ ...createForm, send: e.target.checked })} />
            {a.sendQuote}
          </label>
          <button type="submit" className="btn btn-primary">{d.save}</button>
        </form>
      </DashModal>
    </>
  );
}

const EMPTY_LANG_FORM = {
  code: '', nameFr: '', nameEn: '', nameAr: '', nativeName: '', flag: '', flagUrl: '', isActive: true,
};

export function AdminLanguagesPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [statFilter, setStatFilter] = useState('all');
  const [form, setForm] = useState(EMPTY_LANG_FORM);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmLang, setConfirmLang] = useState(null);
  const [toggleLang, setToggleLang] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const reload = () => adminApi.languages().then((res) => setItems(res.languages || [])).catch(() => setItems([]));
  useEffect(() => { reload(); }, []);

  // Le toast s'affiche quelques secondes puis disparaît
  useEffect(() => {
    if (!msg) return undefined;
    const timer = setTimeout(() => setMsg(''), 3500);
    return () => clearTimeout(timer);
  }, [msg]);

  const q = search.trim().toLowerCase();
  const filtered = items
    .filter((l) => (statFilter === 'active' ? l.isActive : statFilter === 'inactive' ? !l.isActive : true))
    .filter((l) => !q || `${l.code} ${l.nameFr} ${l.nameEn} ${l.nativeName}`.toLowerCase().includes(q));

  const { page, totalPages, pageItems, setPage, total } = usePagination(filtered, 15);

  const langStats = [
    { id: 'all', icon: Languages, label: 'Toutes', value: items.length },
    { id: 'active', icon: CheckCircle2, label: 'Actives', value: items.filter((l) => l.isActive).length },
    { id: 'inactive', icon: XCircle, label: 'Inactives', value: items.filter((l) => !l.isActive).length },
  ];

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_LANG_FORM);
    setFormOpen(true);
  };

  const openEdit = (lang) => {
    setEditing(lang);
    setForm({
      code: lang.code,
      nameFr: lang.nameFr || '',
      nameEn: lang.nameEn || '',
      nameAr: lang.nameAr || '',
      nativeName: lang.nativeName || '',
      flag: lang.flag || '',
      flagUrl: lang.flagUrl || '',
      isActive: lang.isActive !== false,
    });
    setFormOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, flagUrl: previewUrl };
      if (editing) await adminApi.updateLanguage(editing.id, payload);
      else await adminApi.createLanguage(payload);
      setFormOpen(false);
      setMsg(d.saved);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    } finally {
      setBusy(false);
    }
  };

  // Activation et désactivation passent toutes deux par le popup de confirmation.
  const doToggle = async () => {
    if (!toggleLang) return;
    setBusy(true);
    try {
      await adminApi.updateLanguage(toggleLang.id, { isActive: !toggleLang.isActive });
      setToggleLang(null);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!confirmLang) return;
    setBusy(true);
    try {
      await adminApi.deleteLanguage(confirmLang.id);
      setConfirmLang(null);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const previewUrl = langFlagUrl({ flag: form.flag, flagUrl: form.flagUrl });

  return (
    <>
      <header className="dash-page-head orders-hero">
        <div className="orders-hero-top">
          <span className="orders-hero-icon"><Languages size={22} /></span>
          <div className="orders-hero-text" style={{ flex: 1 }}>
            <h1>{a.languagesTitle}</h1>
            <p>{a.languagesSubtitle}</p>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
            onClick={openCreate}
          >
            <Plus size={16} />
            {a.addLanguage}
          </button>
        </div>
        <div className="orders-hero-stats" role="tablist">
          {langStats.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={statFilter === s.id}
              className={`orders-stat${statFilter === s.id ? ' is-active' : ''}`}
              onClick={() => setStatFilter(s.id)}
            >
              <span className="orders-stat-icon"><s.icon size={15} /></span>
              <span className="orders-stat-body">
                <strong>{s.value}</strong>
                <em>{s.label}</em>
              </span>
            </button>
          ))}
        </div>
      </header>
      {msg && (
        <div className="dash-toast dash-toast--float dash-toast--success" role="status">
          <CheckCircle2 size={16} aria-hidden="true" />
          <span>{msg}</span>
          <button type="button" onClick={() => setMsg('')} aria-label="Fermer">
            <X size={13} aria-hidden="true" />
          </button>
        </div>
      )}

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />Filtres</span>
          <span className="of-meta">
            <span>{filtered.length} / {items.length}</span>
            <button type="button" className="of-reset" onClick={() => { setSearch(''); setStatFilter('all'); }}>
              <RotateCcw size={13} />Réinitialiser
            </button>
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>Recherche</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Code, nom FR/EN ou natif…"
            />
          </div>
          <div className="of-field">
            <label>Statut</label>
            <select value={statFilter} onChange={(e) => setStatFilter(e.target.value)}>
              <option value="all">Tous</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>
        </div>
      </div>

      <section className="dash-panel" style={{ marginTop: 16 }}>
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Drapeau</th>
                <th>Code</th>
                <th>FR</th>
                <th>Natif</th>
                <th>{d.status.active}</th>
                <th>{d.actions}</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((lang) => (
                <tr key={lang.id}>
                  <td>
                    {langFlagUrl(lang) ? (
                      <img src={langFlagUrl(lang)} alt="" width={24} style={{ borderRadius: 3, border: '1px solid rgba(0,0,0,.08)' }} />
                    ) : (flagEmoji(lang.flag) || '—')}
                  </td>
                  <td>{lang.code}</td>
                  <td>{lang.nameFr}</td>
                  <td>{lang.nativeName || '—'}</td>
                  <td>
                    <span
                      className="dash-btn-sm"
                      style={{
                        pointerEvents: 'none',
                        color: lang.isActive ? '#15803d' : '#b91c1c',
                        background: lang.isActive ? 'rgba(21,128,61,.08)' : 'rgba(185,28,28,.08)',
                      }}
                    >
                      {lang.isActive ? d.status.active : 'Inactif'}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="dash-btn-sm"
                      onClick={() => openEdit(lang)}
                    >
                      {d.edit}
                    </button>
                    <button
                      type="button"
                      className="dash-btn-sm"
                      style={{ marginInlineStart: 6 }}
                      onClick={() => setToggleLang(lang)}
                    >
                      {lang.isActive ? a.deactivate : a.activate}
                    </button>
                    <button
                      type="button"
                      className="dash-btn-sm"
                      style={{ marginInlineStart: 6 }}
                      onClick={() => setConfirmLang(lang)}
                    >
                      {a.delete}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} />
      </section>

      <DashModal
        open={formOpen}
        title={editing ? `${d.edit} — ${editing.code}` : a.addLanguage}
        subtitle="Code ISO, noms affichés et drapeau représentatif."
        onClose={() => setFormOpen(false)}
      >
        <form className="dash-form" onSubmit={onSubmit}>
          <div className="dform-section" style={{ marginBottom: 14 }}>
            <header className="dform-section-head" style={{ marginBottom: 10 }}>
              <Languages size={16} />
              <h4>Langue</h4>
            </header>
            <div className="dform-grid dform-grid--2">
              <div className="dform-field">
                <label>Code ISO <span className="dform-req">*</span></label>
                <input
                  required
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="FR"
                  maxLength={3}
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
              <div className="dform-field">
                <label>Nom natif</label>
                <input value={form.nativeName} onChange={(e) => setForm({ ...form, nativeName: e.target.value })} placeholder="Français" />
              </div>
              <div className="dform-field">
                <label>Nom (FR) <span className="dform-req">*</span></label>
                <input required value={form.nameFr} onChange={(e) => setForm({ ...form, nameFr: e.target.value })} placeholder="Français" />
              </div>
              <div className="dform-field">
                <label>Nom (EN)</label>
                <input value={form.nameEn} onChange={(e) => setForm({ ...form, nameEn: e.target.value })} placeholder="French" />
              </div>
              <div className="dform-field dform-wide">
                <label>Nom (AR)</label>
                <input value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} placeholder="الفرنسية" dir="rtl" />
              </div>
            </div>
          </div>

          <div className="dform-section" style={{ marginBottom: 14 }}>
            <header className="dform-section-head" style={{ marginBottom: 10 }}>
              <ImagePlus size={16} />
              <h4>Drapeau</h4>
            </header>
            <div style={{ display: 'flex', gap: 14, alignItems: 'stretch' }}>
              <div
                style={{
                  width: 96,
                  minHeight: 72,
                  borderRadius: 10,
                  border: '1px solid rgba(0,0,0,.1)',
                  background: 'rgba(0,0,0,.03)',
                  display: 'grid',
                  placeItems: 'center',
                  overflow: 'hidden',
                  flexShrink: 0,
                  fontSize: 34,
                }}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  flagEmoji(form.flag) || '🌐'
                )}
              </div>
              <div className="dform-grid" style={{ flex: 1, gap: 10 }}>
                <div className="dform-field">
                  <label>Pays représentatif</label>
                  <select value={form.flag} onChange={(e) => setForm({ ...form, flag: e.target.value, flagUrl: '' })}>
                    <option value="">— Aucun / personnalisé —</option>
                    {allCountries.filter((c) => c.code !== 'other').map(({ code, name }) => (
                      <option key={code} value={code}>{name}</option>
                    ))}
                  </select>
                </div>
                <div className="dform-field">
                  <label>URL personnalisée (optionnel)</label>
                  <input
                    value={form.flagUrl}
                    onChange={(e) => setForm({ ...form, flagUrl: e.target.value })}
                    placeholder="https://flagcdn.com/fr.svg"
                  />
                </div>
              </div>
            </div>
          </div>

          <label className="dform-checkbox" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Langue active sur la plateforme
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? '…' : d.save}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setFormOpen(false)}>
              {d.cancel}
            </button>
          </div>
        </form>
      </DashModal>

      <DashModal
        open={Boolean(toggleLang)}
        title={toggleLang?.isActive ? 'Désactiver la langue' : 'Activer la langue'}
        subtitle={toggleLang ? `${toggleLang.nameFr} (${toggleLang.code})` : ''}
        onClose={() => setToggleLang(null)}
      >
        {toggleLang && (
          <div style={{ padding: '4px 2px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                marginBottom: 14,
                borderRadius: 10,
                border: '1px solid rgba(0,0,0,.08)',
                background: 'rgba(0,0,0,.02)',
              }}
            >
              {langFlagUrl(toggleLang) ? (
                <img
                  src={langFlagUrl(toggleLang)}
                  alt=""
                  width={40}
                  style={{ borderRadius: 6, border: '1px solid rgba(0,0,0,.1)', flexShrink: 0 }}
                />
              ) : (
                <span style={{ fontSize: 28, lineHeight: 1 }}>{flagEmoji(toggleLang.flag) || '🌐'}</span>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: 'block' }}>{toggleLang.nameFr}</strong>
                <span style={{ fontSize: '.8rem', opacity: .6 }}>
                  {toggleLang.code}{toggleLang.nativeName ? ` · ${toggleLang.nativeName}` : ''}
                </span>
              </div>
              <span
                className="dash-btn-sm"
                style={{
                  pointerEvents: 'none',
                  color: toggleLang.isActive ? '#15803d' : '#b91c1c',
                  background: toggleLang.isActive ? 'rgba(21,128,61,.08)' : 'rgba(185,28,28,.08)',
                }}
              >
                {toggleLang.isActive ? d.status.active : 'Inactif'}
              </span>
            </div>
            <p style={{ margin: '0 0 18px', fontSize: '.92rem', lineHeight: 1.6 }}>
              {toggleLang.isActive
                ? 'Cette langue ne sera plus proposée aux clients et aux traducteurs.'
                : 'Cette langue sera de nouveau proposée aux clients et aux traducteurs.'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-outline" onClick={() => setToggleLang(null)} disabled={busy}>
                {d.cancel}
              </button>
              <button
                type="button"
                className={`btn ${toggleLang.isActive ? 'btn-danger' : 'btn-primary'}`}
                onClick={doToggle}
                disabled={busy}
              >
                {busy ? '…' : (toggleLang.isActive ? a.deactivate : a.activate)}
              </button>
            </div>
          </div>
        )}
      </DashModal>

      <DashModal
        open={Boolean(confirmLang)}
        title="Supprimer la langue"
        subtitle={confirmLang ? `${confirmLang.nameFr} (${confirmLang.code})` : ''}
        onClose={() => setConfirmLang(null)}
      >
        {confirmLang && (
          <div style={{ padding: '4px 2px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                marginBottom: 14,
                borderRadius: 10,
                border: '1px solid rgba(185,28,28,.18)',
                background: 'rgba(185,28,28,.04)',
              }}
            >
              {langFlagUrl(confirmLang) ? (
                <img
                  src={langFlagUrl(confirmLang)}
                  alt=""
                  width={40}
                  style={{ borderRadius: 6, border: '1px solid rgba(0,0,0,.1)', flexShrink: 0 }}
                />
              ) : (
                <span style={{ fontSize: 28, lineHeight: 1 }}>{flagEmoji(confirmLang.flag) || '🌐'}</span>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: 'block' }}>{confirmLang.nameFr}</strong>
                <span style={{ fontSize: '.8rem', opacity: .6 }}>
                  {confirmLang.code}{confirmLang.nativeName ? ` · ${confirmLang.nativeName}` : ''}
                </span>
              </div>
              <Trash2 size={18} style={{ color: '#b91c1c', flexShrink: 0 }} />
            </div>
            <p style={{ margin: '0 0 18px', fontSize: '.92rem', lineHeight: 1.6 }}>
              Supprimer définitivement cette langue du catalogue ? Cette action est irréversible.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-outline" onClick={() => setConfirmLang(null)} disabled={busy}>
                {d.cancel}
              </button>
              <button type="button" className="btn btn-danger" onClick={doDelete} disabled={busy}>
                {busy ? '…' : a.delete}
              </button>
            </div>
          </div>
        )}
      </DashModal>
    </>
  );
}

// Services principaux fixes (config/services.json) + parents supplémentaires
// utilisés par le formulaire de commande mais absents des pages /services/type/*
const EXTRA_PARENTS = [
  { slug: 'interpreting', name: 'Interprétariat' },
  { slug: 'it', name: 'Informatique (domaine commande)' },
  { slug: 'scientific', name: 'Scientifique (domaine commande)' },
  { slug: 'education', name: 'Éducation (domaine commande)' },
  { slug: 'other', name: 'Autre' },
  { slug: 'certificationDocs', name: 'Certification de documents (commande)' },
];

const TPRICE_TYPES = [
  ['professional', 'Traduction professionnelle'],
  ['certified', 'Traduction certifiée'],
  ['sworn', 'Traduction assermentée'],
];

// Tag de filtre associé à chaque type de traduction autorisé
const AUTH_TYPE_TAGS = { professional: 'general', certified: 'certified', sworn: 'sworn' };

const emptyTypePricing = () => Object.fromEntries(
  TPRICE_TYPES.map(([id]) => [id, { price: '', priceEur: '' }])
);

// typePricing[] (DB) → objet de formulaire indexé par type
const typePricingForForm = (s) => {
  const out = emptyTypePricing();
  (s?.typePricing || []).forEach((e) => {
    if (out[e.type]) {
      out[e.type] = {
        price: e.price || '',
        priceEur: e.priceEur || '',
      };
    }
  });
  return out;
};

const EMPTY_SERVICE = {
  titleFr: '', titleEn: '', titleAr: '', type: '', tags: [], options: [], slug: '', isActive: true,
  description: '', image: '', quote: false, featured: false,
  prices: { document: '', page: '', word: '', hour: '', halfday: '', day: '', service: '' },
  billingUnit: 'document', priceLimit: '', minPrice: '', minQty: '', priceEur: '',
  typePricing: emptyTypePricing(), reqCountry: false, reqOrg: false,
  domains: [], docTypes: [],
  authorizedTypes: TPRICE_TYPES.map(([id]) => id),
  prestation: 'translation', interpChoice: '',
};

// Options « Type de traduction » / « Type de document » (cases à cocher du formulaire)
const TRANSLATION_TYPE_OPTIONS = mainServiceGroups.filter((g) => g.id !== 'documents').flatMap((g) => g.services);
const DOCUMENT_TYPE_OPTIONS = mainServiceGroups.find((g) => g.id === 'documents')?.services || [];

const UNIT_LABELS = {
  document: 'par document', page: 'par page', word: 'par mot',
  hour: 'par heure', halfday: 'par demi-journée', day: 'par journée', service: 'par mission',
};
const UNIT_NAME_LABELS = {
  document: 'Document', page: 'Page', word: 'Mot',
  hour: 'Heure', halfday: 'Demi-journée', day: 'Journée', service: 'Mission',
};

// Tarifs par type affichés sur la carte de la liste
const TPRICE_LABELS = { professional: 'Professionnelle', certified: 'Certifiée', sworn: 'Assermentée' };
const METHOD_LABELS = { document: 'document', page: 'page', word: 'mot' };

// « Général » — option d'interprétariat hors config (supprimée du interpreting.json)
const GENERAL_INTERPRETING = {
  slug: 'generalInterpreting',
  name: 'Interprétariat général',
  nameEn: 'General interpreting',
  nameAr: 'الترجمة الشفوية العامة',
  image: '/images/interpreting-general.jpg',
};

// slug (tag/type) → libellé lisible : services principaux, domaines,
// types de documents, services d'interprétariat, parents additionnels.
const SLUG_LABELS = Object.fromEntries(
  [...mainServices, ...interpretingServices, GENERAL_INTERPRETING, ...EXTRA_PARENTS]
    .filter((s) => s && s.slug && s.name)
    .map((s) => [String(s.slug).toLowerCase(), s.name])
);
const slugLabel = (slug) => SLUG_LABELS[String(slug || '').toLowerCase()] || slug || '—';

// Prix par unité d'un enregistrement DB → état du formulaire (avec fallback basePrice/unit)
const pricesForForm = (s) => {
  const pr = s.prices || {};
  const out = { document: '', page: '', word: '', hour: '', halfday: '', day: '', service: '' };
  for (const k of Object.keys(out)) if (pr[k] > 0) out[k] = pr[k];
  if (!Object.values(out).some(Boolean) && (s.basePrice || 0) > 0 && s.unit && s.unit in out) {
    out[s.unit] = s.basePrice;
  }
  return out;
};

// Liste des prix définis pour l'affichage de la card admin
const priceEntries = (s) => {
  const entries = Object.entries(s.prices || {}).filter(([, v]) => Number(v) > 0);
  if (!entries.length && (s.basePrice || 0) > 0) entries.push([s.unit || '', s.basePrice]);
  return entries;
};

export function AdminServicesPage() {
  const { t, lang } = useLanguage();
  const locale = lang === 'ar' ? 'ar-TN' : lang === 'en' ? 'en-GB' : 'fr-FR';
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [items, setItems] = useState([]);
  const [serviceForm, setServiceForm] = useState(EMPTY_SERVICE);
  const [editingService, setEditingService] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [imgPreview, setImgPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [prestation, setPrestation] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [docFilter, setDocFilter] = useState('all');
  const [ttypeFilter, setTtypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [msg, setMsg] = useState('');
  const [confirmAction, setConfirmAction] = useState(null); // { kind: 'toggle'|'delete', service }
  const [detailService, setDetailService] = useState(null);

  const reload = () => {
    adminApi.services().then((res) => setItems(res.services || [])).catch(() => setItems([]));
  };
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (!msg) return undefined;
    const t = setTimeout(() => setMsg(''), 4500);
    return () => clearTimeout(t);
  }, [msg]);

  const norm = (s) => (s || '').toLowerCase();
  const visibleServices = items.filter((s) => {
    const isInterp = s.category === 'interpreting' || s.type === 'interpreting';
    if (prestation !== 'all' && isInterp !== (prestation === 'interpreting')) return false;
    if (prestation === 'interpreting' && serviceFilter !== 'all') {
      const slug = norm(s.slug);
      if (serviceFilter === 'other') {
        if (interpretingServices.some((i) => norm(i.slug) === slug)) return false;
      } else if (slug !== norm(serviceFilter)) return false;
    }
    if (prestation === 'translation' && serviceFilter !== 'all') {
      const fts = filterTypesFor(s);
      if (serviceFilter === 'other') {
        if (fts.some((f) => TRANSLATION_SLUGS.has(f))) return false;
      } else if (!fts.includes(serviceFilter.toLowerCase())) return false;
    }
    if (prestation === 'translation' && docFilter !== 'all') {
      const fts = filterTypesFor(s);
      if (docFilter === 'other') {
        if (fts.some((f) => DOCUMENT_SLUGS.has(f))) return false;
      } else if (!fts.includes(docFilter.toLowerCase())) return false;
    }
    // Type de traduction autorisé (services autorisés du sous-service)
    if (prestation === 'translation' && ttypeFilter !== 'all') {
      const types = (s.typePricing || []).map((e) => e.type);
      if (!types.includes(ttypeFilter)) return false;
    }
    if (statusFilter !== 'all' && Boolean(s.isActive) !== (statusFilter === 'active')) return false;
    const q = norm(search).trim();
    if (q && !norm(`${s.slug} ${s.titleFr} ${s.titleEn} ${s.titleAr}`).includes(q)) return false;
    return true;
  });

  const pg = usePagination(visibleServices, 12);

  const onPrestationChange = (value) => {
    setServiceForm((f) => ({
      ...f,
      prestation: value, type: '', tags: [], options: [], interpChoice: '',
      domains: [], docTypes: [], authorizedTypes: TPRICE_TYPES.map(([id]) => id),
      billingUnit: value === 'interpreting' ? 'hour' : 'document',
      quote: false, priceEur: '',
    }));
  };

  // Cases « Domaines associés » / « Services autorisés » — multi-sélection
  const toggleInList = (key, value) => {
    setServiceForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));
  };

  // « Tout sélectionner » d'une liste de cases (domaines, types de documents…)
  const allInList = (key, slugs) => slugs.every((s) => serviceForm[key].includes(s));
  const toggleAllInList = (key, slugs, select) => {
    setServiceForm((f) => ({
      ...f,
      [key]: select ? [...new Set([...f[key], ...slugs])] : f[key].filter((v) => !slugs.includes(v)),
    }));
  };

  const setTPRow = (id, patch) => {
    setServiceForm((f) => ({
      ...f,
      typePricing: { ...f.typePricing, [id]: { ...f.typePricing[id], ...patch } },
    }));
  };

  const onServicePrincipalChange = (value) => {
    const conf = value === 'general'
      ? GENERAL_INTERPRETING
      : interpretingServices.find((i) => i.slug === value) || null;
    setServiceForm((f) => ({
      ...f,
      interpChoice: value,
      type: 'interpreting',
      tags: value === 'other' ? [] : [value],
      ...(conf ? { slug: conf.slug } : {}),
      ...(conf ? {
        titleFr: f.titleFr || conf.name,
        titleEn: f.titleEn || conf.nameEn || '',
        titleAr: f.titleAr || conf.nameAr || '',
        image: f.image || conf.image || '',
      } : {}),
    }));
  };

  const setPrice = (unit, value) => {
    setServiceForm((f) => ({ ...f, prices: { ...f.prices, [unit]: value } }));
  };

  const onImageFile = async (file) => {
    if (!file) return;
    if (!/\.(jpe?g|png|webp)$/i.test(file.name)) {
      setMsg('Format non accepté — JPG, JPEG, PNG ou WEBP uniquement.');
      return;
    }
    setImgPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const res = await adminApi.uploadFile(file);
      setServiceForm((f) => ({ ...f, image: res.url }));
    } catch {
      setMsg("Échec de l'envoi de l'image — réessayez ou saisissez un chemin.");
    } finally {
      setUploading(false);
    }
  };

  const submitService = async (e) => {
    e.preventDefault();
    setMsg('');
    const isTranslation = serviceForm.prestation === 'translation';
    if (isTranslation && !serviceForm.authorizedTypes.length) {
      setMsg('Cochez au moins un service autorisé.');
      return;
    }
    const billingUnit = serviceForm.prestation === 'interpreting'
      ? (serviceForm.billingUnit || 'hour')
      : (serviceForm.billingUnit || 'document');
    // Tarifs par service autorisé — la méthode est commune, les prix TND/EUR
    // sont propres à chaque type de traduction.
    const typePricing = isTranslation
      ? serviceForm.authorizedTypes
          .map((id) => {
            const e = serviceForm.typePricing[id] || {};
            const price = Number(e.price) || 0;
            const priceEur = Number(e.priceEur) || 0;
            return price > 0 || priceEur > 0
              ? { type: id, method: billingUnit, price, priceEur, minPrice: 0 }
              : null;
          })
          .filter(Boolean)
      : [];
    if (isTranslation && !serviceForm.quote && !typePricing.some((e) => e.price > 0)) {
      setMsg('Saisissez au moins un prix unitaire en TND (ou choisissez « Prix à confirmer par devis »).');
      return;
    }
    const prices = {};
    if (!serviceForm.quote) {
      if (isTranslation) {
        // Repli « tarif principal » : tarif professionnel, sinon premier tarif saisi.
        const main = typePricing.find((e) => e.type === 'professional' && e.price > 0)
          || typePricing.find((e) => e.price > 0);
        if (main) prices[billingUnit] = main.price;
      } else {
        const n = Number(serviceForm.prices?.[billingUnit]);
        if (n > 0) prices[billingUnit] = n;
        if (!Object.keys(prices).length) {
          setMsg('Le prix en TND est obligatoire (ou choisissez « Prix sur devis »).');
          return;
        }
      }
    }
    // Catégorie + domaines cochés + tags de filtre dérivés des services
    // autorisés → le document apparaît dans toutes les pages/filtres utiles.
    const tags = isTranslation
      ? [...new Set([
          ...serviceForm.docTypes,
          ...serviceForm.domains,
          ...serviceForm.authorizedTypes.map((id) => AUTH_TYPE_TAGS[id]),
        ].filter(Boolean))]
      : serviceForm.tags;
    const payload = {
      titleFr: serviceForm.titleFr,
      titleEn: serviceForm.titleEn,
      titleAr: serviceForm.titleAr,
      type: isTranslation ? (serviceForm.docTypes[0] || serviceForm.domains[0] || 'other') : (serviceForm.type || 'other'),
      tags,
      options: serviceForm.options,
      slug: serviceForm.slug || '',
      description: serviceForm.description,
      image: serviceForm.image,
      isActive: Boolean(serviceForm.isActive),
      featured: Boolean(serviceForm.featured),
      category: serviceForm.prestation === 'interpreting' ? 'interpreting' : 'translation',
      prices,
      unit: Object.keys(prices).length ? billingUnit : '',
      basePrice: prices[billingUnit] || Object.values(prices)[0] || 0,
      priceLimit: billingUnit === 'document' ? (Number(serviceForm.priceLimit) || 0) : 0,
      minPrice: Number(serviceForm.minPrice) || 0,
      minQty: Number(serviceForm.minQty) || 0,
      priceEur: Number(serviceForm.priceEur) || 0,
      typePricing,
      domains: isTranslation ? serviceForm.domains : [],
      requirements: {
        countryOfUse: Boolean(serviceForm.reqCountry),
        recipientOrganization: Boolean(serviceForm.reqOrg),
      },
      priceMax: 0,
    };
    try {
      if (editingService) {
        await adminApi.updateService(editingService.id, payload);
      } else {
        await adminApi.createService(payload);
      }
      closeForm();
      setMsg(editingService ? 'Service mis à jour avec succès.' : 'Service ajouté avec succès.');
      reload();
    } catch (err) {
      setMsg(err.data?.error || err.data?.message || 'Error');
    }
  };

  const editService = (s) => {
    const isInterp = s.category === 'interpreting' || s.type === 'interpreting';
    const interpConf = interpretingServices.find((i) => norm(i.slug) === norm(s.slug));
    setEditingService(s);
    setImgPreview('');
    setServiceForm({
      titleFr: s.titleFr || '', titleEn: s.titleEn || '', titleAr: s.titleAr || '',
      type: s.type || '',
      tags: Array.isArray(s.tags) && s.tags.length
        ? s.tags
        : (TRANSLATION_SLUGS.has(norm(s.type)) || DOCUMENT_SLUGS.has(norm(s.type)) ? [s.type] : []),
      options: Array.isArray(s.options) ? s.options : [],
      slug: s.slug || '', isActive: s.isActive !== false,
      featured: Boolean(s.featured),
      description: s.description || '', image: s.image || '',
      prices: pricesForForm(s),
      quote: !Object.values(pricesForForm(s)).some(Boolean)
        && !(s.typePricing || []).some((e) => Number(e.price) > 0 || Number(e.priceEur) > 0),
      billingUnit: s.unit || (s.typePricing || [])[0]?.method || (isInterp ? 'hour' : 'document'),
      priceEur: s.priceEur > 0 ? s.priceEur : '',
      priceLimit: s.priceLimit > 0 ? s.priceLimit : '',
      minPrice: s.minPrice > 0 ? s.minPrice : '',
      minQty: s.minQty > 0 ? s.minQty : '',
      typePricing: typePricingForForm(s),
      reqCountry: !!s.requirements?.countryOfUse,
      reqOrg: !!s.requirements?.recipientOrganization,
      domains: Array.isArray(s.domains) ? s.domains : [],
      docTypes: [...new Set([
        ...(Array.isArray(s.tags) ? s.tags : []),
        s.type,
      ].filter((tg) => DOCUMENT_SLUGS.has(norm(tg))))],
      authorizedTypes: (s.typePricing || []).length
        ? [...new Set((s.typePricing || []).map((e) => e.type).filter((tp) => TPRICE_TYPES.some(([id]) => id === tp)))]
        : TPRICE_TYPES.map(([id]) => id),
      prestation: isInterp ? 'interpreting' : 'translation',
      interpChoice: isInterp ? (interpConf ? interpConf.slug : (norm(s.slug) === 'generalinterpreting' ? 'general' : 'other')) : '',
    });
    setFormOpen(true);
  };

  const openCreate = () => {
    setEditingService(null);
    setServiceForm(EMPTY_SERVICE);
    setImgPreview('');
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingService(null);
    setServiceForm(EMPTY_SERVICE);
    setImgPreview('');
    setUploading(false);
  };

  const toggleServiceActive = async (s) => {
    try {
      await adminApi.updateService(s.id, { isActive: !s.isActive });
      setMsg(s.isActive ? `« ${s.titleFr} » désactivé.` : `« ${s.titleFr} » activé.`);
      reload();
    } catch (err) {
      setMsg(err.data?.error || err.data?.message || 'Erreur');
    }
  };

  const removeService = async (s) => {
    try {
      await adminApi.deleteService(s.id);
      setMsg('Sous-service supprimé.');
      reload();
    } catch (err) {
      // 409 : utilisé dans des commandes → proposer la désactivation
      setMsg(err.data?.message || err.data?.error || 'Suppression impossible');
      reload();
    }
  };

  const confirmPending = async () => {
    const { kind, service } = confirmAction || {};
    setConfirmAction(null);
    if (!service) return;
    if (kind === 'delete') await removeService(service);
    else await toggleServiceActive(service);
  };

  return (
    <>
      <header className="dash-page-head dash-page-head--split">
        <div className="dash-page-head-text">
          <h1>Gestion des sous-services</h1>
          <p>Types de documents et éléments dynamiques rattachés aux services principaux (définis dans services.json).</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          <Plus size={15} aria-hidden="true" /> Ajouter un sous-service
        </button>
        <div className="cat-banner-stats">
          <div className="cat-banner-stat">
            <em><Layers size={13} aria-hidden="true" />Sous-services</em>
            <strong>{items.length}</strong>
            <small>{visibleServices.length} affichés</small>
          </div>
          <div className="cat-banner-stat">
            <em><CheckCircle2 size={13} aria-hidden="true" />Actifs</em>
            <strong>{items.filter((s) => s.isActive).length}</strong>
          </div>
          <div className="cat-banner-stat">
            <em><XCircle size={13} aria-hidden="true" />Inactifs</em>
            <strong>{items.filter((s) => !s.isActive).length}</strong>
          </div>
          <div className="cat-banner-stat">
            <em><FolderTree size={13} aria-hidden="true" />Services parents</em>
            <strong>{new Set(items.map((s) => s.type)).size}</strong>
          </div>
        </div>
      </header>
      {msg && (
        <div className="dash-toast dash-toast--float" role="status">
          <CheckCircle2 size={16} aria-hidden="true" />
          <span>{msg}</span>
          <button type="button" onClick={() => setMsg('')} aria-label="Fermer">
            <X size={13} aria-hidden="true" />
          </button>
        </div>
      )}

      <div className="cat-toolbar">
        <div className="cat-tabs" role="tablist" aria-label="Type de prestation">
          {[
            { id: 'all', label: 'Toutes', count: items.length },
            { id: 'translation', label: 'Traduction', count: items.filter((s) => s.category !== 'interpreting' && s.type !== 'interpreting').length },
            { id: 'interpreting', label: 'Interprétariat', count: items.filter((s) => s.category === 'interpreting' || s.type === 'interpreting').length },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={prestation === tab.id}
              className={`cat-tab${prestation === tab.id ? ' is-active' : ''}`}
              onClick={() => { setPrestation(tab.id); setServiceFilter('all'); setDocFilter('all'); }}
            >
              {tab.label}
              <span className="cat-tab-count">{tab.count}</span>
            </button>
          ))}
        </div>
        {(search || serviceFilter !== 'all' || docFilter !== 'all' || ttypeFilter !== 'all' || statusFilter !== 'all' || prestation !== 'all') && (
          <div className="cat-toolbar-actions">
            <button
              type="button"
              className="cat-reset"
              onClick={() => { setSearch(''); setPrestation('all'); setServiceFilter('all'); setDocFilter('all'); setTtypeFilter('all'); setStatusFilter('all'); }}
            >
              <RotateCcw size={13} aria-hidden="true" /> Réinitialiser
            </button>
          </div>
        )}
      </div>

      <DashModal
        open={formOpen}
        title={editingService ? `Modifier « ${editingService.titleFr} »` : 'Ajouter un sous-service'}
        subtitle="Sous-service rattaché à un service principal du services.json"
        onClose={closeForm}
        wide
      >
        <form className="dash-form svc-form" onSubmit={submitService}>
          {/* 1. Type de prestation */}
          <section className="dform-section">
            <header className="dform-section-head">
              <Layers size={17} aria-hidden="true" />
              <h4>1. Type de prestation</h4>
            </header>
            <div className="dform-field dform-wide">
              <div className="dform-radio-group">
                {[['translation', 'Traduction'], ['interpreting', 'Interprétariat']].map(([value, label]) => (
                  <label key={value} className="dform-radio">
                    <input
                      type="radio"
                      name="prestation"
                      checked={serviceForm.prestation === value}
                      onChange={() => onPrestationChange(value)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </section>

          {serviceForm.prestation === 'interpreting' && (
            <>
              {/* 2. Service principal */}
              <section className="dform-section">
                <header className="dform-section-head">
                  <FolderTree size={17} aria-hidden="true" />
                  <h4>2. Service principal</h4>
                </header>
                <div className="dform-field dform-wide">
                  <label>Service principal <span className="dform-req">*</span></label>
                  <select required value={serviceForm.interpChoice} onChange={(e) => onServicePrincipalChange(e.target.value)}>
                    <option value="">— Sélectionner —</option>
                    <option value="general">Général</option>
                    {interpretingServices.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                  </select>
                </div>
              </section>

              {/* 3. Informations du service */}
              <section className="dform-section">
                <header className="dform-section-head">
                  <FileText size={17} aria-hidden="true" />
                  <h4>3. Informations du service</h4>
                </header>
                <div className="dform-grid dform-grid--2 dform-grid--compact">
                  <div className="dform-field">
                    <label>Nom du sous-service en français <span className="dform-req">*</span></label>
                    <input
                      required
                      value={serviceForm.titleFr}
                      onChange={(e) => {
                        const v = e.target.value;
                        setServiceForm((f) => ({ ...f, titleFr: v }));
                      }}
                      placeholder="ex. Interprétariat de conférence"
                    />
                  </div>
                  <div className="dform-field">
                    <label>Nom en anglais</label>
                    <input value={serviceForm.titleEn} onChange={(e) => setServiceForm({ ...serviceForm, titleEn: e.target.value })} />
                  </div>
                  <div className="dform-field">
                    <label>Nom en arabe</label>
                    <input value={serviceForm.titleAr} onChange={(e) => setServiceForm({ ...serviceForm, titleAr: e.target.value })} dir="rtl" />
                  </div>
                  <div className="dform-field">
                    <label>Description</label>
                    <input value={serviceForm.description} onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })} />
                  </div>
                </div>
                <div className="dform-field dform-wide">
                  <label>Image du service <em className="svc-price-tag">(facultative)</em></label>
                  <div
                    className="svc-dropzone"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); onImageFile(e.dataTransfer.files?.[0]); }}
                  >
                    <input
                      id="svc-image-file"
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      hidden
                      onChange={(e) => { onImageFile(e.target.files?.[0]); e.target.value = ''; }}
                    />
                    <label htmlFor="svc-image-file" className="svc-dropzone-label">
                      <ImagePlus size={24} aria-hidden="true" />
                      <span>{uploading ? 'Envoi en cours…' : 'Glisser-déposer une image ou cliquer pour sélectionner'}</span>
                      <small>Formats acceptés : JPG, JPEG, PNG, WEBP</small>
                    </label>
                  </div>
                  {(imgPreview || serviceForm.image) && (
                    <div className="svc-image-preview">
                      <img
                        src={imgPreview || assetUrl(serviceForm.image)}
                        alt="Aperçu de l'image"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>
              </section>
            </>
          )}

          {serviceForm.prestation === 'translation' && (
            <>
              {/* 1. Informations générales */}
              <section className="dform-section">
                <header className="dform-section-head">
                  <FileText size={17} aria-hidden="true" />
                  <h4>2. Informations générales</h4>
                </header>
                <div className="dform-grid dform-grid--2 dform-grid--compact">
                  <div className="dform-field">
                    <label>Nom (FR) <span className="dform-req">*</span></label>
                    <input
                      required
                      value={serviceForm.titleFr}
                      onChange={(e) => {
                        const v = e.target.value;
                        setServiceForm((f) => ({ ...f, titleFr: v }));
                      }}
                      placeholder="ex. Acte de naissance"
                    />
                  </div>
                  <div className="dform-field">
                    <label>Nom (EN)</label>
                    <input value={serviceForm.titleEn} onChange={(e) => setServiceForm({ ...serviceForm, titleEn: e.target.value })} />
                  </div>
                  <div className="dform-field">
                    <label>Nom (AR)</label>
                    <input value={serviceForm.titleAr} onChange={(e) => setServiceForm({ ...serviceForm, titleAr: e.target.value })} dir="rtl" />
                  </div>
                  <div className="dform-field">
                    <label>Description</label>
                    <input
                      value={serviceForm.description}
                      onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                      placeholder="ex. Acte d'état civil destiné à une démarche administrative."
                    />
                  </div>
                </div>
              </section>

              {/* 2. Services autorisés pour ce document */}
              <section className="dform-section">
                <header className="dform-section-head">
                  <Layers size={17} aria-hidden="true" />
                  <h4>3. Services autorisés pour ce document</h4>
                </header>
                <div className="dform-field dform-wide">
                  <p className="dform-hint" style={{ marginTop: 0 }}>
                    Le document peut être proposé pour plusieurs services. Chaque service possède son propre tarif.
                  </p>
                  <div className="svc-auth-row">
                    {TPRICE_TYPES.map(([id, label]) => (
                      <label key={id} className="dform-checkbox">
                        <input
                          type="checkbox"
                          checked={serviceForm.authorizedTypes.includes(id)}
                          onChange={() => toggleInList('authorizedTypes', id)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  <small className="dform-hint">
                    La disponibilité réelle dépendra aussi des règles applicables au document et au pays de destination.
                  </small>
                </div>
              </section>

              {/* 3. Domaines associés */}
              <section className="dform-section">
                <header className="dform-section-head">
                  <FolderTree size={17} aria-hidden="true" />
                  <h4>4. Domaines associés</h4>
                </header>
                <p className="dform-hint" style={{ marginTop: 0 }}>
                  Sélectionnez tous les domaines pertinents pour ce document — optionnel.
                </p>
                <div className="dform-grid dform-grid--2">
                  <div className="dform-field">
                    <label>Domaines de traduction</label>
                    <div className="dform-checkbox-scroll">
                      <label className="dform-checkbox" style={{ fontWeight: 600 }}>
                        <input
                          type="checkbox"
                          checked={allInList('domains', TRANSLATION_TYPE_OPTIONS.map((s) => s.slug))}
                          onChange={(e) => toggleAllInList('domains', TRANSLATION_TYPE_OPTIONS.map((s) => s.slug), e.target.checked)}
                        />
                        Tous les domaines
                      </label>
                      {TRANSLATION_TYPE_OPTIONS.map((s) => (
                        <label key={s.slug} className="dform-checkbox">
                          <input
                            type="checkbox"
                            checked={serviceForm.domains.includes(s.slug)}
                            onChange={() => toggleInList('domains', s.slug)}
                          />
                          {s.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="dform-field">
                    <label>Types de documents</label>
                    <div className="dform-checkbox-scroll">
                      <label className="dform-checkbox" style={{ fontWeight: 600 }}>
                        <input
                          type="checkbox"
                          checked={allInList('docTypes', DOCUMENT_TYPE_OPTIONS.map((s) => s.slug))}
                          onChange={(e) => toggleAllInList('docTypes', DOCUMENT_TYPE_OPTIONS.map((s) => s.slug), e.target.checked)}
                        />
                        Tous les documents
                      </label>
                      {DOCUMENT_TYPE_OPTIONS.map((s) => (
                        <label key={s.slug} className="dform-checkbox">
                          <input
                            type="checkbox"
                            checked={serviceForm.docTypes.includes(s.slug)}
                            onChange={() => toggleInList('docTypes', s.slug)}
                          />
                          {s.name}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* 4. Tarification par service */}
              <section className="dform-section">
                <header className="dform-section-head">
                  <Banknote size={17} aria-hidden="true" />
                  <h4>5. Tarification par service</h4>
                </header>
                <div className="dform-field dform-wide">
                  <label>Méthode de calcul <span className="dform-req">*</span></label>
                  <div className="svc-methods">
                    {[
                      { id: 'document', label: 'Prix par document', hint: 'Prix fixe — actes, attestations, passeports…', Icon: FileText },
                      { id: 'page', label: 'Prix par page', hint: 'Dossiers et documents de plusieurs pages', Icon: Files },
                      { id: 'word', label: 'Prix par nombre de mots', hint: 'Textes longs — comptage automatique', Icon: Type },
                    ].map(({ id, label, hint, Icon }) => (
                      <label
                        key={id}
                        className={`svc-method${serviceForm.billingUnit === id && !serviceForm.quote ? ' is-on' : ''}`}
                      >
                        <input
                          type="radio"
                          name="billingUnit"
                          value={id}
                          checked={serviceForm.billingUnit === id && !serviceForm.quote}
                          onChange={() => setServiceForm({ ...serviceForm, billingUnit: id, quote: false })}
                        />
                        <Icon size={18} aria-hidden="true" />
                        <span>
                          <strong>{label}</strong>
                          <small>{hint}</small>
                        </span>
                      </label>
                    ))}
                    <label className={`svc-method${serviceForm.quote ? ' is-on' : ''}`}>
                      <input
                        type="radio"
                        name="billingUnit"
                        checked={serviceForm.quote}
                        onChange={() => setServiceForm({ ...serviceForm, quote: true })}
                      />
                      <Banknote size={18} aria-hidden="true" />
                      <span>
                        <strong>Prix à confirmer par devis</strong>
                        <small>Le tarif est fixé après examen du document</small>
                      </span>
                    </label>
                  </div>
                  {['page', 'word'].includes(serviceForm.billingUnit) && !serviceForm.quote && (
                    <div className="svc-method-limit">
                      <label htmlFor="svc-min-qty">Minimum facturable ({serviceForm.billingUnit === 'word' ? 'mots' : 'pages'})</label>
                      <div className="svc-price">
                        <input
                          id="svc-min-qty"
                          type="number"
                          min="0"
                          value={serviceForm.minQty}
                          onChange={(e) => setServiceForm({ ...serviceForm, minQty: e.target.value })}
                          placeholder="ex. 1"
                        />
                        <span>{serviceForm.billingUnit === 'word' ? 'mots' : 'p.'}</span>
                      </div>
                      <small>Si le volume analysé est inférieur, ce minimum est facturé.</small>
                    </div>
                  )}
                </div>
                {!serviceForm.quote && serviceForm.authorizedTypes.length > 0 && (
                  <div className="svc-tcards">
                    {serviceForm.authorizedTypes.map((id) => {
                      const row = serviceForm.typePricing[id] || {};
                      const label = TPRICE_TYPES.find(([t]) => t === id)?.[1] || id;
                      return (
                        <div className="svc-tcard" key={id}>
                          <strong className="svc-tcard-title">{label}</strong>
                          <div className="dform-field">
                            <label>Prix unitaire (TND)</label>
                            <div className="svc-price">
                              <input
                                type="number"
                                min="0"
                                step="0.001"
                                value={row.price}
                                onChange={(e) => setTPRow(id, { price: e.target.value })}
                                placeholder="0"
                              />
                              <span>DT</span>
                            </div>
                          </div>
                          <div className="dform-field">
                            <label>Prix unitaire (EUR)</label>
                            <div className="svc-price">
                              <input
                                type="number"
                                min="0"
                                step="0.001"
                                value={row.priceEur}
                                onChange={(e) => setTPRow(id, { priceEur: e.target.value })}
                                placeholder="0"
                              />
                              <span>€</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

            </>
          )}

          {/* Image — traduction uniquement (l'interprétariat l'intègre dans « Informations ») */}
          {serviceForm.prestation === 'translation' && (
          <section className="dform-section">
            <header className="dform-section-head">
              <ImagePlus size={17} aria-hidden="true" />
              <h4>Image du service</h4>
            </header>
            <div
              className="svc-dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onImageFile(e.dataTransfer.files?.[0]); }}
            >
              <input
                id="svc-image-file"
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                hidden
                onChange={(e) => { onImageFile(e.target.files?.[0]); e.target.value = ''; }}
              />
              <label htmlFor="svc-image-file" className="svc-dropzone-label">
                <ImagePlus size={24} aria-hidden="true" />
                <span>{uploading ? 'Envoi en cours…' : 'Glissez-déposez une image ici ou cliquez pour sélectionner un fichier.'}</span>
                <small>Formats acceptés : JPG, JPEG, PNG, WEBP</small>
              </label>
            </div>
            {(imgPreview || serviceForm.image) && (
              <div className="svc-image-preview">
                <img
                  src={imgPreview || assetUrl(serviceForm.image)}
                  alt="Aperçu de l'image"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </section>
          )}

          {serviceForm.prestation === 'interpreting' && (
            <>
          {/* 4. Tarification */}
          <section className="dform-section">
            <header className="dform-section-head">
              <Banknote size={17} aria-hidden="true" />
              <h4>4. Tarification</h4>
            </header>
            <div className="dform-field dform-wide">
              <label>Méthode de tarification <span className="dform-req">*</span></label>
              <div className="svc-methods">
                {[
                  { id: 'hour', label: 'Prix par heure', hint: 'Facturation à l\'heure de mission', Icon: Clock },
                  { id: 'halfday', label: 'Prix par demi-journée', hint: 'Bloc de ~4 heures', Icon: Timer },
                  { id: 'day', label: 'Prix par journée', hint: 'Bloc de ~8 heures', Icon: CalendarDays },
                  { id: 'service', label: 'Prix par mission', hint: 'Forfait unique par mission', Icon: Banknote },
                ].map(({ id, label, hint, Icon }) => (
                  <label
                    key={id}
                    className={`svc-method${serviceForm.billingUnit === id && !serviceForm.quote ? ' is-on' : ''}`}
                  >
                    <input
                      type="radio"
                      name="billingUnit"
                      value={id}
                      checked={serviceForm.billingUnit === id && !serviceForm.quote}
                      onChange={() => setServiceForm({ ...serviceForm, billingUnit: id, quote: false })}
                    />
                    <Icon size={18} aria-hidden="true" />
                    <span>
                      <strong>{label}</strong>
                      <small>{hint}</small>
                    </span>
                  </label>
                ))}
                <label className={`svc-method${serviceForm.quote ? ' is-on' : ''}`}>
                  <input
                    type="radio"
                    name="billingUnit"
                    checked={serviceForm.quote}
                    onChange={() => setServiceForm({ ...serviceForm, quote: true })}
                  />
                  <Banknote size={18} aria-hidden="true" />
                  <span>
                    <strong>Prix sur devis</strong>
                    <small>Le tarif est fixé après étude de la demande</small>
                  </span>
                </label>
              </div>
            </div>
            {!serviceForm.quote && (
              <div className="dform-grid dform-grid--2 dform-grid--compact">
                <div className="dform-field">
                  <label>Prix en TND <span className="dform-req">*</span></label>
                  <div className="svc-price">
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      required
                      value={serviceForm.prices[serviceForm.billingUnit]}
                      onChange={(e) => setPrice(serviceForm.billingUnit, e.target.value)}
                      placeholder="0"
                    />
                    <span>DT</span>
                  </div>
                </div>
                <div className="dform-field">
                  <label>Prix en EUR <span className="dform-req">*</span></label>
                  <div className="svc-price">
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={serviceForm.priceEur}
                      onChange={(e) => setServiceForm({ ...serviceForm, priceEur: e.target.value })}
                      placeholder="0"
                    />
                    <span>€</span>
                  </div>
                </div>
              </div>
            )}
          </section>
            </>
          )}



          {/* Publication + Page d'accueil — même ligne */}
          <section className="dform-section">
            <header className="dform-section-head">
              <CheckCircle2 size={17} aria-hidden="true" />
              <h4>Publication</h4>
            </header>
            <div className="dform-grid dform-grid--2" style={{ alignItems: 'center' }}>
              <div className="dform-field">
                <label>Statut</label>
                <div className="svc-auth-row">
                  <label className="dform-checkbox">
                    <input
                      type="radio"
                      name="svc-status"
                      checked={serviceForm.isActive}
                      onChange={() => setServiceForm({ ...serviceForm, isActive: true })}
                    />
                    Actif
                  </label>
                  <label className="dform-checkbox">
                    <input
                      type="radio"
                      name="svc-status"
                      checked={!serviceForm.isActive}
                      onChange={() => setServiceForm({ ...serviceForm, isActive: false })}
                    />
                    Inactif
                  </label>
                </div>
              </div>
              <div className="dform-field">
                <label>Page d'accueil</label>
                <div className="svc-auth-row">
                  <label className="dform-checkbox">
                    <input
                      type="checkbox"
                      checked={serviceForm.featured}
                      onChange={(e) => setServiceForm({ ...serviceForm, featured: e.target.checked })}
                    />
                    Afficher dans le carrousel de la page d'accueil
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* 8. Enregistrement */}
          <footer className="dform-footer">
            {msg && <p className="svc-form-error" role="alert">{msg}</p>}
            <span className="dform-spacer" />
            <button type="button" className="btn btn-outline" onClick={closeForm}>
              {d.cancel || 'Annuler'}
            </button>
            <button type="submit" className="btn btn-primary">Enregistrer le service</button>
          </footer>
        </form>
      </DashModal>

      {/* Confirmation activer / désactiver / supprimer */}
      <DashModal
        open={Boolean(confirmAction)}
        title={
          confirmAction?.kind === 'delete'
            ? 'Supprimer le service'
            : confirmAction?.service?.isActive
              ? 'Désactiver le service'
              : 'Activer le service'
        }
        onClose={() => setConfirmAction(null)}
      >
        {confirmAction && (
          <div className="svc-confirm">
            <p>
              {confirmAction.kind === 'delete'
                ? `Voulez-vous vraiment supprimer « ${confirmAction.service.titleFr} » ? Cette action est définitive.`
                : confirmAction.service.isActive
                  ? `Voulez-vous désactiver « ${confirmAction.service.titleFr} » ? Il ne sera plus visible sur le site.`
                  : `Voulez-vous activer « ${confirmAction.service.titleFr} » ? Il sera visible sur le site.`}
            </p>
            <div className="svc-confirm-actions">
              <button type="button" className="btn btn-outline" onClick={() => setConfirmAction(null)}>
                Annuler
              </button>
              <button
                type="button"
                className={`btn ${confirmAction.kind === 'delete' ? 'btn-danger' : 'btn-primary'}`}
                onClick={confirmPending}
              >
                Confirmer
              </button>
            </div>
          </div>
        )}
      </DashModal>

      {/* Détails du document / sous-service */}
      <DashModal
        open={Boolean(detailService)}
        title={detailService?.titleFr || 'Détails'}
        medium
        onClose={() => setDetailService(null)}
      >
        {detailService && (() => {
          const s = detailService;
          const isInterp = s.category === 'interpreting' || s.type === 'interpreting';
          const tp = (s.typePricing || []).filter((e) => Number(e.price) > 0);
          const entries = priceEntries(s);
          const tags = Array.isArray(s.tags) ? s.tags : [];
          const docTags = tags.filter((tg) => DOCUMENT_SLUGS.has(norm(tg)));
          const domainSlugs = (Array.isArray(s.domains) && s.domains.length
            ? s.domains
            : tags.filter((tg) => TRANSLATION_SLUGS.has(norm(tg))));
          const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(locale) : '—');
          return (
            <div className="svc-detail">
              <div className="svc-detail-head">
                {s.image ? (
                  <img src={assetUrl(s.image)} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <span className="svc-detail-thumb"><FileText size={22} aria-hidden="true" /></span>
                )}
                <div>
                  <h3>{s.titleFr}</h3>
                  <code className="svc-detail-code">{s.slug}</code>
                  <div className="svc-detail-badges">
                    <StatusBadge status={s.isActive ? 'active' : 'cancelled'} labels={{ active: 'Actif', cancelled: 'Inactif' }} />
                    <span className="svc-detail-chip">{isInterp ? 'Interprétariat' : 'Traduction'}</span>
                    {s.featured && <span className="svc-detail-chip svc-detail-chip--accent">Page d'accueil</span>}
                  </div>
                </div>
              </div>

              {(s.titleEn || s.titleAr) && (
                <dl className="svc-detail-grid">
                  {s.titleEn && <div><dt>Nom (EN)</dt><dd>{s.titleEn}</dd></div>}
                  {s.titleAr && <div><dt>Nom (AR)</dt><dd dir="rtl">{s.titleAr}</dd></div>}
                </dl>
              )}
              {s.description && <p className="svc-detail-desc">{s.description}</p>}

              {isInterp ? (
                <dl className="svc-detail-grid">
                  <div><dt>Service principal</dt><dd>{slugLabel(tags.find((tg) => interpretingServices.some((i) => norm(i.slug) === norm(tg))) || s.type)}</dd></div>
                </dl>
              ) : (
                <>
                  {docTags.length > 0 && (
                    <div className="svc-detail-block">
                      <h5>Types de documents</h5>
                      <div className="svc-detail-chips">{docTags.map((tg) => <span key={tg} className="svc-detail-chip">{slugLabel(tg)}</span>)}</div>
                    </div>
                  )}
                  {domainSlugs.length > 0 && (
                    <div className="svc-detail-block">
                      <h5>Domaines associés</h5>
                      <div className="svc-detail-chips">{domainSlugs.map((tg) => <span key={tg} className="svc-detail-chip">{slugLabel(tg)}</span>)}</div>
                    </div>
                  )}
                </>
              )}

              <div className="svc-detail-block">
                <h5>Tarification</h5>
                {tp.length ? (
                  <span className="admin-svc-tprices">
                    {tp.map((e) => (
                      <span key={e.type} className={`admin-svc-tprice admin-svc-tprice--${e.type}`}>
                        <span className="admin-svc-tprice-type">{TPRICE_LABELS[e.type] || e.type}</span>
                        <span className="admin-svc-tprice-vals">
                          <strong>{e.price} TND</strong>
                          <span className="admin-svc-tprice-eur">
                            {e.priceEur > 0 ? `${e.priceEur} €` : formatEur(tndToEur(Number(e.price)), locale)}
                          </span>
                          <em>par {METHOD_LABELS[e.method] || 'document'}</em>
                        </span>
                      </span>
                    ))}
                  </span>
                ) : entries.length ? (
                  <span className="admin-svc-tprices">
                    {entries.map(([u, p]) => (
                      <span key={u || 'fixed'} className="admin-svc-tprice admin-svc-tprice--unit">
                        <span className="admin-svc-tprice-type">{UNIT_NAME_LABELS[u] || 'Tarif'}</span>
                        <span className="admin-svc-tprice-vals">
                          <strong>{p} TND</strong>
                          <span className="admin-svc-tprice-eur">
                            {Number(s.priceEur) > 0 ? `${s.priceEur} €` : formatEur(tndToEur(Number(p)), locale)}
                          </span>
                          {UNIT_LABELS[u] ? <em>{UNIT_LABELS[u]}</em> : null}
                        </span>
                      </span>
                    ))}
                  </span>
                ) : (
                  <p className="svc-detail-empty">Prix sur devis — fixé après étude de la demande.</p>
                )}
              </div>

              {(s.requirements?.countryOfUse || s.requirements?.recipientOrganization || (s.options || []).length > 0 || Number(s.minQty) > 0 || Number(s.minPrice) > 0) && (
                <dl className="svc-detail-grid">
                  {Number(s.minQty) > 0 && <div><dt>Minimum facturable</dt><dd>{s.minQty} {s.unit === 'word' ? 'mots' : s.unit === 'page' ? 'pages' : 'unités'}</dd></div>}
                  {Number(s.minPrice) > 0 && <div><dt>Minimum de facturation</dt><dd>{s.minPrice} TND</dd></div>}
                  {s.requirements?.countryOfUse && <div><dt>Exigé à la commande</dt><dd>Pays d'utilisation</dd></div>}
                  {s.requirements?.recipientOrganization && <div><dt>Exigé à la commande</dt><dd>Organisme destinataire</dd></div>}
                  {(s.options || []).length > 0 && <div><dt>Options</dt><dd>{s.options.join(', ')}</dd></div>}
                </dl>
              )}

              <dl className="svc-detail-grid svc-detail-grid--meta">
                <div><dt>Créé le</dt><dd>{fmtDate(s.createdAt)}</dd></div>
                <div><dt>Modifié le</dt><dd>{fmtDate(s.updatedAt)}</dd></div>
                <div><dt>Commandes</dt><dd>{s.orders || 0}</dd></div>
              </dl>

              <footer className="dform-footer">
                <span className="dform-spacer" />
                <button type="button" className="btn btn-outline" onClick={() => setDetailService(null)}>Fermer</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => { const svc = detailService; setDetailService(null); editService(svc); }}
                >
                  <Pencil size={14} aria-hidden="true" /> Modifier
                </button>
              </footer>
            </div>
          );
        })()}
      </DashModal>

      <section className="dash-panel" style={{ marginTop: 16 }}>
        <div className="cat-filterbar">
          <div className="cat-search">
            <Search size={15} aria-hidden="true" />
            <input
              placeholder="Rechercher (nom, slug)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Rechercher un sous-service"
            />
          </div>
          {prestation === 'translation' && (
            <select value={ttypeFilter} onChange={(e) => setTtypeFilter(e.target.value)} aria-label="Type de traduction">
              <option value="all">Type de traduction — Tous</option>
              {TPRICE_TYPES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          )}
          {prestation !== 'all' && (
            <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} aria-label="Service">
              <option value="all">Service — Tous</option>
              {(prestation === 'interpreting'
                ? interpretingServices.map((s) => ({ slug: s.slug, name: s.name }))
                : mainServiceGroups.filter((g) => g.id !== 'documents').flatMap((g) => g.services.map((s) => ({ slug: s.slug, name: s.name })))
              ).map((o) => <option key={o.slug} value={o.slug}>{o.name}</option>)}
              <option value="other">Autre</option>
            </select>
          )}
          {prestation === 'translation' && (
            <select value={docFilter} onChange={(e) => setDocFilter(e.target.value)} aria-label="Type de document">
              <option value="all">Type de document — Tous</option>
              {(mainServiceGroups.find((g) => g.id === 'documents')?.services || []).map((s) => (
                <option key={s.slug} value={s.slug}>{s.name}</option>
              ))}
              <option value="other">Autre (etc.)</option>
            </select>
          )}
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filtrer par statut">
            <option value="all">Tous statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </select>
          <span className="cat-count">{visibleServices.length} / {items.length}</span>
        </div>
        {visibleServices.length === 0 ? (
          <p style={{ textAlign: 'center', opacity: 0.6, padding: '24px 0' }}>Aucun sous-service</p>
        ) : (
          <div className="all-services-grid admin-svc-grid">
            {pg.pageItems.map((s) => {
              const entries = priceEntries(s);
              const typePrices = (s.typePricing || []).filter((e) => Number(e.price) > 0);
              const reviewCount = s.reviewsCount || 0;
              return (
                <div key={s.id} className={`all-services-card admin-svc-card${s.isActive ? '' : ' is-inactive'}`}>
                  <div className="all-services-card-media">
                    {s.image && (
                      <img
                        src={assetUrl(s.image)}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <span className="admin-svc-status">
                      <StatusBadge status={s.isActive ? 'active' : 'cancelled'} labels={{ active: 'Actif', cancelled: 'Inactif' }} />
                    </span>
                  </div>
                  <div className="all-services-card-body">
                    <h3>{s.titleFr}</h3>
                    <div className="all-services-card-rating">
                      <div className="all-services-card-stars" aria-label={`${reviewCount} avis`}>
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={14} fill={reviewCount > 0 ? 'currentColor' : 'none'} aria-hidden="true" />
                        ))}
                      </div>
                      <span className="all-services-card-reviews">
                        {reviewCount} avis{s.ordersCount ? ` · ${s.ordersCount} cmd` : ''}
                      </span>
                    </div>
                    <p className="all-services-card-price">
                      {typePrices.length ? (
                        <span className="admin-svc-tprices">
                          {typePrices.map((e) => (
                            <span key={e.type} className={`admin-svc-tprice admin-svc-tprice--${e.type}`}>
                              <span className="admin-svc-tprice-type">{TPRICE_LABELS[e.type] || e.type}</span>
                              <span className="admin-svc-tprice-vals">
                                <strong>{e.price} TND</strong>
                                <span className="admin-svc-tprice-eur">
                                  {e.priceEur > 0 ? `${e.priceEur} €` : formatEur(tndToEur(Number(e.price)), locale)}
                                </span>
                                <em>par {METHOD_LABELS[e.method] || 'document'}</em>
                              </span>
                            </span>
                          ))}
                        </span>
                      ) : entries.length ? (
                        <span className="admin-svc-tprices">
                          {entries.map(([u, p]) => (
                            <span key={u || 'fixed'} className="admin-svc-tprice admin-svc-tprice--unit">
                              <span className="admin-svc-tprice-type">{UNIT_NAME_LABELS[u] || 'Tarif'}</span>
                              <span className="admin-svc-tprice-vals">
                                <strong>{p} TND</strong>
                                <span className="admin-svc-tprice-eur">
                                  {Number(s.priceEur) > 0 ? `${s.priceEur} €` : formatEur(tndToEur(Number(p)), locale)}
                                </span>
                                {UNIT_LABELS[u] ? <em>{UNIT_LABELS[u]}</em> : null}
                              </span>
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="all-services-card-from">Sur devis</span>
                      )}
                    </p>
                    <div className="admin-svc-actions translator-card__actions">
                      <button
                        type="button"
                        className="translator-card__btn"
                        onClick={() => setDetailService(s)}
                      >
                        <Eye size={14} aria-hidden="true" /> Détails
                      </button>
                      <button
                        type="button"
                        className="translator-card__btn"
                        onClick={() => editService(s)}
                      >
                        <Pencil size={14} aria-hidden="true" /> {d.edit || 'Modifier'}
                      </button>
                      <button
                        type="button"
                        className={`translator-card__btn ${s.isActive ? 'translator-card__btn--danger' : 'translator-card__btn--success'}`}
                        onClick={() => setConfirmAction({ kind: 'toggle', service: s })}
                      >
                        <Power size={14} aria-hidden="true" /> {s.isActive ? a.deactivate : a.activate}
                      </button>
                      <button
                        type="button"
                        className="translator-card__btn translator-card__btn--danger"
                        onClick={() => setConfirmAction({ kind: 'delete', service: s })}
                      >
                        <Trash2 size={14} aria-hidden="true" /> {a.delete}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Pagination page={pg.page} totalPages={pg.totalPages} total={pg.total} onChange={pg.setPage} />
      </section>
    </>
  );
}

export function AdminInvoicesPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderId, setOrderId] = useState('');
  const invoicesPg = usePagination(invoices, 6);
  const [msg, setMsg] = useState('');

  const reload = () => {
    adminApi.invoices().then((res) => setInvoices(res.invoices || [])).catch(() => setInvoices([]));
    adminApi.orders().then((res) => setOrders(res.orders || [])).catch(() => setOrders([]));
  };
  useEffect(() => { reload(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createInvoice({ orderId });
      setOrderId('');
      setMsg(d.saved);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{a.invoicesTitle}</h1>
        <p>{a.invoicesSubtitle}</p>
      </header>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel">
        <form className="dash-form" onSubmit={create} style={{ maxWidth: 420 }}>
          <div className="dash-field">
            <label>{a.ordersTitle}</label>
            <select required value={orderId} onChange={(e) => setOrderId(e.target.value)}>
              <option value="">—</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>{o.id} · {o.document} · {o.client}</option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn btn-primary">{a.createInvoice}</button>
        </form>
      </section>
      <section className="dash-panel" style={{ marginTop: 16 }}>
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{a.colClient}</th>
                <th>{a.colTotal}</th>
                <th>{a.colStatus}</th>
                <th>{d.actions}</th>
              </tr>
            </thead>
            <tbody>
              {invoicesPg.pageItems.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.id}</td>
                  <td>{inv.client || '—'}</td>
                  <td>{inv.amount} {inv.currency}</td>
                  <td><StatusBadge status={inv.status} labels={d.status} /></td>
                  <td>
                    {inv.status !== 'paid' && (
                      <button type="button" className="dash-btn-sm" onClick={async () => { await adminApi.updateInvoice(inv.id, { status: 'paid' }); reload(); }}>
                        {a.markPaid}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={invoicesPg.page} totalPages={invoicesPg.totalPages} total={invoicesPg.total} onChange={invoicesPg.setPage} />
      </section>
    </>
  );
}

const ROLE_LABELS = { client: 'Client', translator: 'Pro', lawyer: 'Avocat', notary: 'Notaire' };

export function AdminMessagesPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [toId, setToId] = useState('');
  const [body, setBody] = useState('');
  const [threadKey, setThreadKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const threads = useMemo(() => {
    const map = new Map();
    messages.forEach((m) => {
      if (!map.has(m.threadKey)) map.set(m.threadKey, { key: m.threadKey, people: new Map(), last: m.createdAt, preview: '' });
      const thread = map.get(m.threadKey);
      [m.from, m.to].forEach((p) => {
        if (p && p.role !== 'admin') thread.people.set(p.id, p);
      });
      if (new Date(m.createdAt) >= new Date(thread.last || 0)) {
        thread.last = m.createdAt;
        thread.preview = m.body;
      }
    });
    return Array.from(map.values())
      .sort((x, y) => new Date(y.last || 0) - new Date(x.last || 0))
      .map((thread) => ({
        ...thread,
        name: Array.from(thread.people.values()).map((p) => p.name).filter(Boolean).join(' ↔ ') || thread.key,
      }));
  }, [messages]);

  const reload = () => {
    adminApi.messages().then((res) => setMessages(res.messages || [])).catch(() => setMessages([]));
  };

  useEffect(() => {
    reload();
    adminApi.users().then((res) => setUsers((res.users || []).filter((u) => u.role !== 'admin'))).catch(() => setUsers([]));
    const timer = setInterval(reload, 4000);
    return () => clearInterval(timer);
  }, []);

  const pickThread = (key) => {
    setThreadKey(key);
    if (key === 'new' || !key) {
      setToId('');
      return;
    }
    const last = [...messages].reverse().find((m) => m.threadKey === key && m.from?.role !== 'admin');
    const thread = threads.find((x) => x.key === key);
    setToId(last?.from?.id || Array.from(thread?.people.keys() || [])[0] || '');
  };

  const filtered = threadKey && threadKey !== 'new' ? messages.filter((m) => m.threadKey === threadKey) : [];

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !toId) return;
    setBusy(true);
    setMsg('');
    try {
      const existing = threadKey && threadKey !== 'new';
      await adminApi.sendMessage({ toId, body, ...(existing ? { threadKey } : {}) });
      const recipient = users.find((u) => u.id === toId);
      const key = existing
        ? threadKey
        : `${{ client: 'client', translator: 'pro', lawyer: 'verifier', notary: 'verifier' }[recipient?.role] || 'pro'}:${toId}`;
      setBody('');
      reload();
      setThreadKey(key);
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{a.messagesTitle}</h1>
        <p>{a.messagesSubtitle}</p>
      </header>
      {msg && <p className="dash-toast">{msg}</p>}
      <MessengerPanel
        threads={threads}
        activeKey={threadKey}
        onSelect={pickThread}
        messages={filtered}
        myRole="admin"
        body={body}
        setBody={setBody}
        onSend={send}
        busy={busy}
        labels={{
          conversations: 'Conversations',
          newMessage: 'Nouveau message',
          selectConversation: 'Sélectionnez une conversation',
          writeMessage: 'Écrire un message…',
          empty: d.empty,
          send: a.sendMessage,
        }}
        contacts={users.map((u) => ({ key: u.id, name: u.name, sub: ROLE_LABELS[u.role] || u.role }))}
        contactKey={toId}
        onPickContact={setToId}
        contactsTitle={a.recipient}
        requireContact
      />
    </>
  );
}

const PAYMENT_METHOD_LABELS = {
  card: 'Carte bancaire',
  transfer: 'Virement',
  virement: 'Virement',
  cash: 'Espèces',
  paypal: 'PayPal',
  other: 'Autre',
};

const methodLabel = (m) => PAYMENT_METHOD_LABELS[m] || m || '—';

export function AdminPaymentsPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState(null);
  const [verifyAction, setVerifyAction] = useState(null); // { type: 'confirm'|'reject', payment }
  const [verifyBusy, setVerifyBusy] = useState(false);

  const reload = () => adminApi.payments().then((res) => setPayments(res.payments || [])).catch(() => setPayments([]));
  useEffect(() => { reload(); }, []);

  const pendingTransfers = payments.filter(
    (p) => p.status === 'pending' && ['transfer', 'virement', 'cash'].includes(p.method)
  );

  const runVerifyAction = async () => {
    if (!verifyAction) return;
    setVerifyBusy(true);
    try {
      if (verifyAction.type === 'confirm') await adminApi.confirmPayment(verifyAction.payment._id);
      else await adminApi.rejectPayment(verifyAction.payment._id);
      setVerifyAction(null);
      reload();
    } catch (err) {
      setVerifyAction(null);
    } finally {
      setVerifyBusy(false);
    }
  };

  const sum = (rows) => rows.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const stats = [
    { icon: Banknote, label: 'Total payé', value: sum(payments.filter((p) => p.status === 'paid')) },
    { icon: Clock, label: 'En attente', value: sum(payments.filter((p) => p.status === 'pending')) },
    { icon: RotateCcw, label: 'Remboursé', value: sum(payments.filter((p) => p.status === 'refunded')) },
  ];

  const filtered = payments.filter((p) => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (methodFilter !== 'all' && (p.method || 'card') !== methodFilter) return false;
    const at = p.paidAt || p.createdAt || '';
    if (dateFrom && at < dateFrom) return false;
    if (dateTo && at > dateTo) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [p.id, p.orderCode, p.quoteCode, p.client, p.clientEmail, p.reference]
      .some((x) => String(x || '').toLowerCase().includes(q));
  });
  const paymentsPg = usePagination(filtered, 12);

  const payDate = (p) => p.paidAt || p.createdAt || '—';

  return (
    <>
      <header className="dash-page-head">
        <h1>{a.paymentsTitle || 'Paiements'}</h1>
        <p>{a.paymentsSubtitle || 'Suivre les paiements, remboursements et transactions des commandes.'}</p>
      </header>

      <div className="cp-stats">
        {stats.map((k) => (
          <div className="dash-stat cp-stat" key={k.label}>
            <span className="cp-stat-icon"><k.icon size={15} /></span>
            <span>{k.label}</span>
            <strong>{k.value} TND</strong>
          </div>
        ))}
      </div>

      {pendingTransfers.length > 0 && (
        <section className="dash-panel" style={{ border: '1px solid #fcd34d', background: '#fffbeb', marginBottom: 18 }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '1rem', margin: '4px 0 12px' }}>
            <Landmark size={17} /> Virements à vérifier
            <span className="orders-panel-count">{pendingTransfers.length}</span>
          </h2>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Commande</th>
                  <th>Client</th>
                  <th>Montant attendu</th>
                  <th>Référence</th>
                  <th>Demandé le</th>
                  <th style={{ textAlign: 'right' }}>Après vérification du relevé bancaire</th>
                </tr>
              </thead>
              <tbody>
                {pendingTransfers.map((p) => (
                  <tr key={p.id}>
                    <td><strong>{p.orderCode || p.quoteCode || '—'}</strong></td>
                    <td>{p.client || '—'}</td>
                    <td><strong>{p.amount} {p.currency || 'TND'}</strong></td>
                    <td><code>{p.reference || `TAR-${p.orderCode || '—'}`}</code></td>
                    <td>{p.createdAt || '—'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <button
                          type="button"
                          className="dash-btn-sm"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#047857' }}
                          onClick={() => setVerifyAction({ type: 'confirm', payment: p })}
                        >
                          <CheckCircle2 size={13} /> Confirmer le paiement
                        </button>
                        <button
                          type="button"
                          className="dash-btn-sm"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#b91c1c' }}
                          onClick={() => setVerifyAction({ type: 'reject', payment: p })}
                        >
                          <XCircle size={13} /> Non reçu
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#92400e', margin: '10px 0 4px' }}>
            <AlertTriangle size={13} /> Ne confirmez un paiement qu’après avoir vérifié le crédit effectif
            (montant + référence) sur le compte bancaire — pas sur la base d’un justificatif envoyé par le client.
          </p>
        </section>
      )}

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />Filtres</span>
          <span className="of-meta">
            <button
              type="button"
              className="of-reset"
              onClick={() => { setSearch(''); setStatusFilter('all'); setMethodFilter('all'); setDateFrom(''); setDateTo(''); }}
            >
              <RotateCcw size={13} />Réinitialiser
            </button>
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>Recherche</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une transaction…"
            />
          </div>
          <div className="of-field">
            <label>{d.statusLabel || 'Statut'}</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tous</option>
              <option value="pending">{d.payment?.pending || 'En attente'}</option>
              <option value="paid">{d.payment?.paid || 'Payé'}</option>
              <option value="failed">{d.payment?.failed || 'Échoué'}</option>
              <option value="refunded">{d.payment?.refunded || 'Remboursé'}</option>
            </select>
          </div>
          <div className="of-field">
            <label>Méthode</label>
            <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}>
              <option value="all">Toutes</option>
              <option value="card">Carte bancaire</option>
              <option value="transfer">Virement</option>
              <option value="other">Autre</option>
            </select>
          </div>
          <div className="of-field">
            <label>Du</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="of-field">
            <label>Au</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="orders-panel-head">
        <h2>{a.paymentsTitle || 'Paiements'}</h2>
        <span className="orders-panel-count">{filtered.length}</span>
      </div>

      <section className="dash-panel">
        {paymentsPg.pageItems.length === 0 ? (
          <p className="dash-empty">{d.empty}</p>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Transaction</th>
                  <th>Commande</th>
                  <th>{a.colClient}</th>
                  <th>Montant</th>
                  <th>Méthode</th>
                  <th>{a.colStatus}</th>
                  <th>Date</th>
                  <th>{d.actions}</th>
                </tr>
              </thead>
              <tbody>
                {paymentsPg.pageItems.map((p) => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>{p.orderCode || p.quoteCode || '—'}</td>
                    <td>{p.client || '—'}</td>
                    <td><strong>{p.amount} {p.currency || 'TND'}</strong></td>
                    <td>{methodLabel(p.method)}</td>
                    <td><StatusBadge status={p.status} labels={d.payment} /></td>
                    <td>{payDate(p)}</td>
                    <td>
                      <button type="button" className="dash-btn-sm" onClick={() => setDetail(p)}>
                        <Eye size={13} /> {d.view || 'Voir'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={paymentsPg.page} totalPages={paymentsPg.totalPages} total={paymentsPg.total} onChange={paymentsPg.setPage} />
      </section>

      <DashModal
        open={Boolean(detail)}
        title={detail ? `Paiement ${detail.id}` : ''}
        subtitle={detail?.orderCode || detail?.quoteCode || ''}
        onClose={() => setDetail(null)}
      >
        {detail && (
          <div style={{ padding: '4px 2px' }}>
            <dl className="od-dl" style={{ marginBottom: 16 }}>
              <div className="od-row"><span className="od-label">Commande</span><span className="od-value">{detail.orderCode || detail.quoteCode || '—'}</span></div>
              <div className="od-row"><span className="od-label">Client</span><span className="od-value">{detail.client || '—'}{detail.clientEmail ? ` · ${detail.clientEmail}` : ''}</span></div>
              <div className="od-row"><span className="od-label">Montant</span><span className="od-value"><strong>{detail.amount} {detail.currency || 'TND'}</strong></span></div>
              <div className="od-row"><span className="od-label">Méthode</span><span className="od-value">{methodLabel(detail.method)}</span></div>
              <div className="od-row"><span className="od-label">Statut</span><span className="od-value"><StatusBadge status={detail.status} labels={d.payment} /></span></div>
              <div className="od-row"><span className="od-label">Référence</span><span className="od-value">{detail.reference || '—'}</span></div>
              <div className="od-row"><span className="od-label">Date</span><span className="od-value">{payDate(detail)}</span></div>
            </dl>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-outline" onClick={() => setDetail(null)}>Fermer</button>
              {detail.orderCode && (
                <Link className="btn btn-primary" to={`/admin/orders/${detail.orderCode}`} onClick={() => setDetail(null)}>
                  Voir la commande <ArrowRight size={14} />
                </Link>
              )}
            </div>
          </div>
        )}
      </DashModal>

      <DashModal
        open={Boolean(verifyAction)}
        title={verifyAction?.type === 'confirm' ? 'Confirmer le paiement' : 'Virement non reçu'}
        subtitle={verifyAction ? `${verifyAction.payment.orderCode || ''} — ${verifyAction.payment.amount} ${verifyAction.payment.currency || 'TND'}` : ''}
        onClose={() => !verifyBusy && setVerifyAction(null)}
      >
        {verifyAction && (
          <div style={{ padding: '4px 2px' }}>
            {verifyAction.type === 'confirm' ? (
              <p style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 12px', fontSize: '0.88rem' }}>
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>
                  Confirmez uniquement si le crédit est <strong>effectif sur le relevé bancaire</strong>
                  {' '}(montant {verifyAction.payment.amount} {verifyAction.payment.currency || 'TND'},
                  référence <code>{verifyAction.payment.reference || `TAR-${verifyAction.payment.orderCode}`}</code>).
                  Une capture ou un justificatif du client ne suffit pas — le virement peut être en cours, rejeté ou annulé.
                </span>
              </p>
            ) : (
              <p style={{ fontSize: '0.9rem' }}>
                Le virement annoncé n’a pas été crédité ? Le paiement sera marqué <strong>échoué</strong>
                {' '}et le client sera notifié pour régler à nouveau sa commande.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button type="button" className="btn btn-outline" onClick={() => setVerifyAction(null)} disabled={verifyBusy}>
                Annuler
              </button>
              <button
                type="button"
                className={`btn ${verifyAction.type === 'confirm' ? 'btn-primary' : 'btn-outline'}`}
                style={verifyAction.type === 'reject' ? { color: '#b91c1c', borderColor: '#fca5a5' } : undefined}
                onClick={runVerifyAction}
                disabled={verifyBusy}
              >
                {verifyBusy ? 'Traitement…' : verifyAction.type === 'confirm' ? 'Crédit vérifié — confirmer' : 'Marquer non reçu'}
              </button>
            </div>
          </div>
        )}
      </DashModal>
    </>
  );
}

export function AdminNotificationsPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [emails, setEmails] = useState([]);

  const reload = () => adminApi.notifications().then((res) => setItems(res.notifications || [])).catch(() => setItems([]));
  useEffect(() => {
    reload();
    adminApi.emailLogs({ page: 1 })
      .then((res) => setEmails((res.logs || []).filter((l) => l.type !== 'ack').slice(0, 8)))
      .catch(() => {});
    const timer = setInterval(reload, 10000);
    return () => clearInterval(timer);
  }, []);

  const openNotif = (n) => {
    if (!n.readAt) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
      adminApi.readNotifications({ id: n.id }).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await adminApi.readNotifications();
      setItems((prev) => prev.map((x) => ({ ...x, readAt: x.readAt || new Date().toISOString() })));
    } catch { /* ignore */ }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{a.notificationsTitle || 'Notifications'}</h1>
        <p>{a.notificationsSubtitle || 'Alertes administrateur.'}</p>
      </header>
      <NotificationsPanel
        items={items}
        onOpen={openNotif}
        onMarkAll={markAll}
        labels={{ all: 'Toutes', unread: 'Non lues', markAllRead: a.markAllRead || 'Tout marquer comme lu', empty: d.empty }}
      />

      <section className="elog-mails">
        <div className="elog-mails-head">
          <h2><Mail size={16} /> E-mails envoyés</h2>
          <Link className="dash-link" to="/admin/email-logs">Tout voir <ArrowRight size={13} /></Link>
        </div>
        {emails.length === 0 ? (
          <p className="dash-empty">Aucun e-mail envoyé.</p>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Objet</th>
                  <th>De / À</th>
                  <th>Message</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((l) => (
                  <tr key={l.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/admin/email-logs')}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {new Date(l.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td><strong>{l.subject}</strong></td>
                    <td>{l.senderName ? <>{l.senderName} <small style={{ color: 'var(--dash-muted)' }}>· {l.senderEmail}</small></> : l.to}</td>
                    <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.preview || '—'}</td>
                    <td>
                      <span className={`dash-badge dash-badge--${l.status === 'sent' ? 'paid' : 'cancelled'}`}>
                        {l.status === 'sent' ? 'Envoyé' : 'Échec'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

// ── Options de livraison (catalogue administrable) ─────────────────────────

const DLV_ICON_CHOICES = [
  { id: 'mail', label: 'E-mail', Icon: Mail },
  { id: 'store', label: 'Boutique', Icon: Store },
  { id: 'truck', label: 'Camion', Icon: Truck },
  { id: 'zap', label: 'Express', Icon: Zap },
];

const EMPTY_DLV_FORM = {
  code: '', labelFr: '', labelEn: '', labelAr: '', description: '',
  icon: 'truck', fee: '', countryFeesText: '', requiresAddress: false,
  isActive: true, sortOrder: 0,
};

// « Pays = tarif » par ligne → objet { Pays: tarif }
function parseCountryFees(text) {
  const out = {};
  String(text || '').split('\n').forEach((line) => {
    const [name, raw] = line.split('=');
    const n = Number(String(raw || '').trim());
    if (name && name.trim() && Number.isFinite(n) && n >= 0) out[name.trim()] = n;
  });
  return out;
}

function countryFeesText(fees) {
  return Object.entries(fees || {}).map(([k, v]) => `${k} = ${v}`).join('\n');
}

export function AdminDeliveryPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_DLV_FORM);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const reload = () => adminApi.deliveryOptions().then((res) => setItems(res.options || [])).catch(() => setItems([]));
  useEffect(() => { reload(); }, []);

  useEffect(() => {
    if (!msg) return undefined;
    const t = setTimeout(() => setMsg(''), 3500);
    return () => clearTimeout(t);
  }, [msg]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_DLV_FORM); setFormOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({
      code: o.code,
      labelFr: o.labelFr || '',
      labelEn: o.labelEn || '',
      labelAr: o.labelAr || '',
      description: o.description || '',
      icon: o.icon || 'truck',
      fee: o.fee == null ? '' : o.fee,
      countryFeesText: countryFeesText(o.countryFees),
      requiresAddress: !!o.requiresAddress,
      isActive: o.isActive !== false,
      sortOrder: o.sortOrder || 0,
    });
    setFormOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        code: form.code,
        labelFr: form.labelFr,
        labelEn: form.labelEn,
        labelAr: form.labelAr,
        description: form.description,
        icon: form.icon,
        fee: form.fee === '' ? null : Number(form.fee),
        countryFees: parseCountryFees(form.countryFeesText),
        requiresAddress: form.requiresAddress,
        isActive: form.isActive,
        sortOrder: Number(form.sortOrder) || 0,
      };
      if (editing) await adminApi.updateDeliveryOption(editing.id || editing._id, payload);
      else await adminApi.createDeliveryOption(payload);
      setFormOpen(false);
      setMsg('Enregistré.');
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (o) => {
    try {
      await adminApi.updateDeliveryOption(o.id || o._id, { isActive: !o.isActive });
      reload();
    } catch { /* silencieux */ }
  };

  const remove = async (o) => {
    if (!window.confirm(`Supprimer « ${o.labelFr} » ?`)) return;
    try {
      await adminApi.deleteDeliveryOption(o.id || o._id);
      reload();
    } catch { /* silencieux */ }
  };

  const seed = async () => {
    try {
      const res = await adminApi.seedDeliveryOptions();
      setMsg(`${res.created} option(s) créée(s).`);
      reload();
    } catch { setMsg('Error'); }
  };

  const feeLabel = (o) => {
    const zones = Object.keys(o.countryFees || {}).length;
    if (o.fee == null && !zones) return 'Sur devis';
    const base = o.fee != null ? `${o.fee} TND` : '—';
    return zones ? `${base} + ${zones} tarif(s) pays` : base;
  };

  return (
    <>
      <header className="dash-page-head orders-hero">
        <div className="orders-hero-top">
          <span className="orders-hero-icon"><Truck size={22} /></span>
          <div className="orders-hero-text" style={{ flex: 1 }}>
            <h1>Modes de livraison</h1>
            <p>Options proposées au client à la commande — libellés, tarifs et tarifs par pays de destination.</p>
          </div>
          {!items.length && (
            <button type="button" className="btn btn-secondary" onClick={seed}>
              Options par défaut
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
            onClick={openCreate}
          >
            <Plus size={16} />
            Ajouter une option
          </button>
        </div>
      </header>

      {msg && <p className="dash-toast">{msg}</p>}

      <section className="dash-card">
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Option</th>
                <th>Code</th>
                <th>Tarif</th>
                <th>Adresse</th>
                <th>Statut</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => {
                const { Icon } = DLV_ICON_CHOICES.find((i) => i.id === o.icon) || { Icon: Truck };
                return (
                  <tr key={o.id || o._id}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Icon size={16} />
                        <span>
                          <strong>{o.labelFr}</strong>
                          {o.description && <><br /><small style={{ color: 'var(--gray-500)' }}>{o.description}</small></>}
                        </span>
                      </span>
                    </td>
                    <td><code>{o.code}</code></td>
                    <td>{feeLabel(o)}</td>
                    <td>{o.requiresAddress ? 'Requise' : '—'}</td>
                    <td>
                      <StatusBadge
                        status={o.isActive ? 'active' : 'inactive'}
                        labels={{ active: 'Active', inactive: 'Inactive' }}
                      />
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className="dash-btn-sm"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                          onClick={() => openEdit(o)}
                        >
                          <Pencil size={13} /> Modifier
                        </button>
                        <button
                          type="button"
                          className="dash-btn-sm"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: o.isActive ? '#b91c1c' : '#047857' }}
                          onClick={() => toggle(o)}
                        >
                          <Power size={13} /> {o.isActive ? 'Désactiver' : 'Activer'}
                        </button>
                        <button
                          type="button"
                          className="dash-btn-sm"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#b91c1c' }}
                          onClick={() => remove(o)}
                        >
                          <Trash2 size={13} /> Supprimer
                        </button>
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!items.length && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--gray-500)' }}>Aucune option — cliquez sur « Options par défaut » ou ajoutez-en une.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <DashModal
        open={formOpen}
        title={editing ? `Modifier — ${editing.labelFr}` : 'Nouvelle option de livraison'}
        subtitle="Code interne, libellés affichés au client et règles tarifaires."
        onClose={() => setFormOpen(false)}
      >
        <form className="dash-form" onSubmit={onSubmit}>
          <div className="dform-section" style={{ marginBottom: 14 }}>
            <header className="dform-section-head" style={{ marginBottom: 10 }}>
              <Truck size={16} />
              <h4>Option</h4>
            </header>
            <div className="dform-grid dform-grid--2">
              <div className="dform-field">
                <label>Code <span className="dform-req">*</span></label>
                <input
                  required
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="post"
                  disabled={!!editing}
                />
                <small>Identifiant technique (digital, pickup, post, express…)</small>
              </div>
              <div className="dform-field">
                <label>Icône</label>
                <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}>
                  {DLV_ICON_CHOICES.map(({ id, label }) => <option key={id} value={id}>{label}</option>)}
                </select>
              </div>
              <div className="dform-field">
                <label>Libellé (FR) <span className="dform-req">*</span></label>
                <input required value={form.labelFr} onChange={(e) => setForm({ ...form, labelFr: e.target.value })} placeholder="Livraison par la poste" />
              </div>
              <div className="dform-field">
                <label>Libellé (EN)</label>
                <input value={form.labelEn} onChange={(e) => setForm({ ...form, labelEn: e.target.value })} placeholder="Postal delivery" />
              </div>
              <div className="dform-field dform-wide">
                <label>Libellé (AR)</label>
                <input value={form.labelAr} onChange={(e) => setForm({ ...form, labelAr: e.target.value })} dir="rtl" />
              </div>
              <div className="dform-field dform-wide">
                <label>Description affichée au client</label>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Envoi postal standard à l'adresse indiquée." />
              </div>
            </div>
          </div>

          <div className="dform-section" style={{ marginBottom: 14 }}>
            <header className="dform-section-head" style={{ marginBottom: 10 }}>
              <Banknote size={16} />
              <h4>Tarification</h4>
            </header>
            <div className="dform-grid dform-grid--2">
              <div className="dform-field">
                <label>Tarif par défaut (TND)</label>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={form.fee}
                  onChange={(e) => setForm({ ...form, fee: e.target.value })}
                  placeholder="0 = gratuit · vide = sur devis"
                />
                <small>0 → « Sans frais » affiché ; vide → « frais selon la destination ».</small>
              </div>
              <div className="dform-field">
                <label>Ordre d'affichage</label>
                <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
              </div>
              <div className="dform-field dform-wide">
                <label>Tarifs par pays de destination</label>
                <textarea
                  rows={4}
                  value={form.countryFeesText}
                  onChange={(e) => setForm({ ...form, countryFeesText: e.target.value })}
                  placeholder={'Tunisie = 8\nFrance = 25\nItalie = 22'}
                  style={{ fontFamily: 'monospace' }}
                />
                <small>Un pays par ligne, format « Pays = tarif ». Prime sur le tarif par défaut.</small>
              </div>
            </div>
          </div>

          <label className="dform-checkbox" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={form.requiresAddress}
              onChange={(e) => setForm({ ...form, requiresAddress: e.target.checked })}
            />
            Nécessite une adresse de livraison (envoi physique)
          </label>
          <label className="dform-checkbox" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Option active sur la plateforme
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? '…' : 'Enregistrer'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setFormOpen(false)}>
              Annuler
            </button>
          </div>
        </form>
      </DashModal>
    </>
  );
}


const EMAIL_TYPE_OPTIONS = [
  { value: '', label: 'Tous les types' },
  { value: 'general', label: 'Question générale' },
  { value: 'quote', label: 'Demande de devis' },
  { value: 'order', label: 'Suivi de commande' },
  { value: 'sworn', label: 'Traduction assermentée' },
  { value: 'partnership', label: 'Partenariat' },
  { value: 'other', label: 'Autre' },
];

export function AdminEmailLogsPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0 });
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ tag: '', q: '', date: '' });
  const [detail, setDetail] = useState(null);
  const [showHtml, setShowHtml] = useState(false);
  const [loading, setLoading] = useState(false);

  const reload = (p = 1) => {
    setLoading(true);
    adminApi.emailLogs({ ...filters, page: p })
      .then((res) => {
        setItems(res.logs || []);
        setStats(res.stats || { total: 0, sent: 0, failed: 0 });
        setPage(res.page || 1);
        setPages(res.pages || 1);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(1); }, [filters.tag, filters.date]);

  const openDetail = (log) => {
    setShowHtml(false);
    setDetail(log);
    adminApi.emailLog(log.id).then((res) => setDetail(res.log)).catch(() => {});
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <>
      <header className="dash-page-head">
        <h1>Historique des e-mails</h1>
        <p>E-mails émis par Tarjemlek — le statut « Envoyé » signifie que le serveur de messagerie a accepté le message.</p>
      </header>

      <div className="admin-kpi-grid" style={{ marginBottom: 16 }}>
        <div className="admin-kpi">
          <span className="admin-kpi-icon"><Mail size={18} /></span>
          <div><strong>{stats.total}</strong><small>Total envoyé</small></div>
        </div>
        <div className="admin-kpi">
          <span className="admin-kpi-icon" style={{ color: 'var(--dash-teal, #1a7a7a)' }}><MailCheck size={18} /></span>
          <div><strong>{stats.sent}</strong><small>Envoyés avec succès</small></div>
        </div>
        <div className="admin-kpi">
          <span className="admin-kpi-icon" style={{ color: '#dc2626' }}><XCircle size={18} /></span>
          <div><strong>{stats.failed}</strong><small>Échecs</small></div>
        </div>
      </div>

      <div className="elog-filters">
        <div className="elog-field elog-search">
          <span>Destinataire</span>
          <Search size={14} />
          <input
            placeholder="Rechercher un e-mail, nom ou objet…"
            value={filters.q}
            onChange={(e) => set('q', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && reload(1)}
          />
        </div>
        <label className="elog-field">
          <span>Type d'e-mail</span>
          <select value={filters.tag} onChange={(e) => set('tag', e.target.value)}>
            {EMAIL_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="elog-field">
          <span>Date</span>
          <input type="date" value={filters.date} onChange={(e) => set('date', e.target.value)} />
        </label>
        <button type="button" className="elog-apply" onClick={() => reload(1)}>
          <SlidersHorizontal size={14} /> Filtrer
        </button>
      </div>

      {items.length === 0 ? (
        <p className="dash-empty">{loading ? 'Chargement…' : 'Aucun e-mail enregistré.'}</p>
      ) : (
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Objet</th>
                <th>De / À</th>
                <th>Type</th>
                <th>Commande</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{new Date(l.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                  <td>
                    <strong>{l.subject}</strong>
                    {l.preview && <div style={{ fontSize: '0.76rem', color: 'var(--dash-muted)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.preview}</div>}
                  </td>
                  <td>{l.senderName ? <>{l.senderName} <small style={{ color: 'var(--dash-muted)' }}>· {l.senderEmail}</small></> : l.to}</td>
                  <td>{l.tagLabel || l.typeLabel}</td>
                  <td>{l.orderRef || '—'}</td>
                  <td>
                    <span className={`dash-badge dash-badge--${l.status === 'sent' ? 'paid' : 'cancelled'}`}>
                      {l.status === 'sent' ? 'Envoyé' : 'Échec'}
                    </span>
                  </td>
                  <td>
                    <button type="button" className="dash-btn-sm" onClick={() => openDetail(l)}>
                      <Eye size={13} /> Détails
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="dash-actions" style={{ marginTop: 10 }}>
          <button type="button" className="dash-btn-sm" disabled={page <= 1} onClick={() => reload(page - 1)}>← Précédent</button>
          <span style={{ fontSize: '0.85rem' }}>Page {page} / {pages}</span>
          <button type="button" className="dash-btn-sm" disabled={page >= pages} onClick={() => reload(page + 1)}>Suivant →</button>
        </div>
      )}

      <DashModal
        open={!!detail}
        title={detail?.subject || 'Détail de l’e-mail'}
        subtitle={`${detail?.senderName ? `${detail.senderName} · ${detail.senderEmail}` : (detail?.to || '')} · ${detail?.createdAt ? new Date(detail.createdAt).toLocaleString('fr-FR') : ''}`}
        onClose={() => setDetail(null)}
      >
        {detail && (
          <div className="dash-form">
            <ul className="dash-file-list">
              {detail.senderName && <li><span>Expéditeur</span><strong>{detail.senderName} · {detail.senderEmail}</strong></li>}
              {!detail.senderName && <li><span>Destinataire</span><strong>{detail.to}</strong></li>}
              <li><span>Type</span><strong>{detail.typeLabel}</strong></li>
              <li><span>Statut</span><strong>{detail.status === 'sent' ? 'Envoyé' : 'Échec'}</strong></li>
              {detail.orderRef && <li><span>Commande</span><strong>{detail.orderRef}</strong></li>}
            </ul>
            {detail.error && (
              <div className="dform-field">
                <label>Erreur serveur</label>
                <div style={{ whiteSpace: 'pre-wrap', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: '10px 12px', fontSize: '0.85rem' }}>
                  {detail.error}
                </div>
              </div>
            )}
            <div className="dform-field">
              <label>Contenu envoyé</label>
              <div className="dform-filters" style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <button type="button" className={`assign-chip ${!showHtml ? 'is-active' : ''}`} onClick={() => setShowHtml(false)}>Texte</button>
                <button type="button" className={`assign-chip ${showHtml ? 'is-active' : ''}`} onClick={() => setShowHtml(true)}>Aperçu HTML</button>
              </div>
              {showHtml && detail.html ? (
                <div
                  style={{ border: '1px solid var(--dash-line)', borderRadius: 10, overflow: 'auto', maxHeight: 420, background: '#f8fafc' }}
                  dangerouslySetInnerHTML={{ __html: detail.html }}
                />
        
              ) : (
                <div style={{ whiteSpace: 'pre-wrap', background: '#f8fafc', border: '1px solid var(--dash-line)', borderRadius: 10, padding: '10px 12px', fontSize: '0.85rem', maxHeight: 320, overflow: 'auto' }}>
                  {detail.text || 'Aucun contenu texte enregistré.'}
                </div>
              )}
            </div>
          </div>
        )}
      </DashModal>
    </>
  );
}
